import React, { useState, useEffect, useMemo, useRef } from 'react';
import { auth, db, rtdb } from '../firebase';
import { doc, updateDoc, getDoc, collection, query, where, getDocs, setDoc, deleteDoc, Timestamp, orderBy, limit, arrayUnion } from 'firebase/firestore';
import { ref as rtdbRef, onValue, query as rtdbQuery, orderByChild, limitToLast, off } from 'firebase/database';
import ReactModal from 'react-modal';
import { FaBuilding, FaSpinner, FaCheck, FaTimes, FaMapMarkerAlt, FaPhone, FaTimesCircle, FaCheckCircle, FaCreditCard, FaPlay, FaEye, FaPause, FaPlus, FaTrash, FaPen, FaChartLine, FaChartBar, FaTimes as FaClose, FaClock, FaBolt, FaFilter, FaSearch, FaExclamationTriangle, FaExternalLinkAlt, FaRedo, FaCopy } from 'react-icons/fa';
import { getCampaignCreativeIds, getAssignedCreativeIdsFromCampaign } from '../utils/campaignCreativeIds';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import dayjs from 'dayjs';
import './CampaignDetail.css';

const API_BASE = 'https://vue-payment-server.fly.dev';
const PULSEFLOW_API = 'https://pulseflow.vuebillboards.com';
const PERION_API_BRIDGE = 'https://perion-api-bridge.fly.dev';
const ANALYTICS_API = 'https://vue-analytics-api.fly.dev';
const SIGNS_QTY_API = 'https://signs.vuebillboards.com';

const CHART_COLORS = {
  primary: '#0b7fd4',
  success: '#107c10',
  warning: '#ffb900',
  danger: '#d13438',
  purple: '#8b5cf6',
  teal: '#14b8a6',
  orange: '#f59e0b',
  pink: '#ec4899'
};

/** True when a per-publisher status means the creative was POSTed / synced with the DSP (not local upload/pending). */
const publisherStatusIndicatesSubmittedToPerion = (raw) => {
  const v = String(raw || '').toLowerCase().trim();
  if (!v) return false;
  // Local / pre-submit workflow — still needs Vue approve + bridge POST in admin.
  if (v === 'pending' || v === 'uploaded' || v === 'draft') return false;
  return v === 'submitted' || v === 'approved' || v === 'rejected' || v === 'partial';
};

/**
 * Moderation states where we may already have deterministic dspCreativeId* fields locally
 * before a successful Perion POST — do not treat those IDs as proof of submission.
 */
const isPreSubmitModerationStatus = (moderationStatus) => {
  const ms = String(moderationStatus || '').toLowerCase().trim();
  return ms === 'uploaded' || ms === 'pending' || ms === 'draft';
};

/** True if this creative is already in the Perion / DSP pipeline (hide Vue pre-approval; do not re-POST). */
const isCreativeSubmittedToPerion = (d) => {
  if (!d || typeof d !== 'object') return false;
  if (d.dspSubmittedAt) return true;
  const ms = String(d.moderationStatus || '').toLowerCase().trim();
  if (ms === 'submitted') return true;
  // IDs alone are unreliable while still uploaded/pending/draft — rely on dspSubmittedAt / moderation / publisher statuses.
  if (isPreSubmitModerationStatus(d.moderationStatus)) {
    if (d.statuses && typeof d.statuses === 'object') {
      return Object.values(d.statuses).some(publisherStatusIndicatesSubmittedToPerion);
    }
    return false;
  }
  const single = String(d.dspCreativeId || d.dspCreativeID || d.dspId || '').trim();
  if (single) return true;
  if (Array.isArray(d.dspCreativeIds) && d.dspCreativeIds.some((x) => String(x || '').trim())) return true;
  const map = d.dspCreativeIdMap;
  if (map && typeof map === 'object' && Object.values(map).some((x) => String(x || '').trim())) return true;
  if (d.statuses && typeof d.statuses === 'object') {
    if (Object.values(d.statuses).some(publisherStatusIndicatesSubmittedToPerion)) return true;
  }
  return false;
};

const cleanAdvertiserDomainInput = (domain) => {
  if (!domain) return '';
  let cleaned = String(domain).trim();
  cleaned = cleaned.replace(/^https?:\/\//i, '');
  cleaned = cleaned.replace(/^www\./i, '');
  cleaned = cleaned.split('/')[0];
  cleaned = cleaned.split(':')[0];
  return cleaned;
};

/** Normalize IAB code from Firestore (string or { code } / legacy shapes). */
const normalizeStoredIabCode = (raw) => {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'object') {
    const c = raw.code ?? raw.iab ?? raw.value ?? raw.id;
    if (c != null && String(c).trim() !== '') return String(c).trim();
    return '';
  }
  const s = String(raw).trim();
  return s === '[object Object]' ? '' : s;
};

/** Resolve default advertiser domain for Hivestack submit (creative → campaign client fields → buyer website). */
const getAdvertiserDomainDefaultsForApprove = (creative, campaign, userInfo) => {
  const candidates = [
    [creative?.advertiser_domain, 'Creative (advertiser_domain)'],
    [creative?.clientDomain, 'Creative (clientDomain)'],
    [creative?.domain, 'Creative (domain)'],
    [campaign?.clientWebsite, 'Campaign (clientWebsite)'],
    [campaign?.clientDomain, 'Campaign (clientDomain)'],
    [campaign?.client_domain, 'Campaign (client_domain)'],
    [campaign?.advertiser_domain, 'Campaign (advertiser_domain)'],
    [campaign?.advertiserDomain, 'Campaign (advertiserDomain)'],
    [userInfo?.website, 'Buyer account (website)'],
  ];
  for (const [raw, label] of candidates) {
    if (raw == null || String(raw).trim() === '') continue;
    const cleaned = cleanAdvertiserDomainInput(raw);
    if (cleaned) return { domain: cleaned, sourceLabel: label };
  }
  return { domain: '', sourceLabel: null };
};

const MOBILE_MQ = '(max-width: 768px)';

const useIsMobile = () => {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const sync = () => setMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  return mobile;
};

const SUB_MODAL_STYLE = {
  overlay: { inset: 0, zIndex: 11000 },
  content: {
    inset: 'unset',
    position: 'relative',
    border: 'none',
    padding: 0,
    overflow: 'hidden',
    borderRadius: 0,
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
  },
};

const getCanonicalDspCreativeId = (sellerId, firebaseCreativeId) =>
  `VUE-CL-${String(sellerId || '').trim()}_${String(firebaseCreativeId || '').trim()}`;

const getUsedDspCreativeIds = (creative) => {
  const used = new Set();
  const add = (v) => {
    const s = String(v || '').trim();
    if (s) used.add(s);
  };
  add(creative?.dspCreativeId);
  add(creative?.dspCreativeID);
  add(creative?.dspId);
  if (Array.isArray(creative?.dspCreativeIds)) creative.dspCreativeIds.forEach(add);
  if (creative?.dspCreativeIdMap && typeof creative.dspCreativeIdMap === 'object') {
    Object.values(creative.dspCreativeIdMap).forEach(add);
  }
  if (Array.isArray(creative?.supersededDspCreativeIds)) creative.supersededDspCreativeIds.forEach(add);
  return used;
};

const isTruthyFlag = (v) => v === true || v === 'true' || v === 1 || v === '1';

const toFiniteNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const formatCampaignTimestamp = (raw) => {
  if (raw == null || raw === '') return null;
  try {
    let d = null;
    if (typeof raw.toDate === 'function') d = raw.toDate();
    else if (raw instanceof Date) d = raw;
    else if (typeof raw.seconds === 'number') d = new Date(raw.seconds * 1000);
    else if (typeof raw._seconds === 'number') d = new Date(raw._seconds * 1000);
    else d = new Date(raw);
    if (!d || Number.isNaN(d.getTime())) return null;
    return dayjs(d).format('MMM D, YYYY h:mm A');
  } catch {
    return null;
  }
};

const websiteHref = (website) => {
  const w = String(website || '').trim();
  if (!w) return '';
  if (/^https?:\/\//i.test(w)) return w;
  return `https://${w}`;
};

/** Checkout Custom Design Package from inHouseCreative + creativeDetailsInfo. */
const getCustomDesignAddon = (campaign) => {
  const info = campaign?.creativeDetailsInfo && typeof campaign.creativeDetailsInfo === 'object'
    ? campaign.creativeDetailsInfo
    : {};
  const companyName = String(info.companyName || '').trim();
  const website = String(info.website || '').trim();
  const creativeCopy = String(info.creativeCopy || info.customDesignDetails || '').trim();
  const logoImageUrl = String(info.logoImageUrl || info.logoImage || '').trim();
  const selected = isTruthyFlag(campaign?.inHouseCreative) || isTruthyFlag(campaign?.customDesignSelected);
  const price = toFiniteNumber(campaign?.customDesignPrice ?? campaign?.campaignCustomDesignPrice, 0);
  const hasDetails = !!(companyName || website || creativeCopy || logoImageUrl);
  return {
    selected,
    utilized: selected || hasDetails,
    price,
    companyName,
    website,
    creativeCopy,
    logoImageUrl,
    hasDetails,
  };
};

/** Campaign photo-op add-on + optional session-fund checkout. */
const getPhotoOpAddon = (campaign) => {
  const selected = isTruthyFlag(campaign?.photoOp) || isTruthyFlag(campaign?.photoOpSelected);
  const price = toFiniteNumber(campaign?.photoOpPrice ?? campaign?.campaignPhotoOpPrice, 0);
  const payment = campaign?.photoOpSessionFundPayment && typeof campaign.photoOpSessionFundPayment === 'object'
    ? campaign.photoOpSessionFundPayment
    : null;
  const pending = campaign?.photoOpPendingLink && typeof campaign.photoOpPendingLink === 'object'
    ? campaign.photoOpPendingLink
    : null;
  const dueUsd = toFiniteNumber(campaign?.photoOpSessionFundDueUsd, 0);
  const hasSessionFund = !!(payment || pending || dueUsd > 0);
  return {
    selected,
    utilized: selected || hasSessionFund,
    price,
    payment,
    pending,
    dueUsd,
    hasSessionFund,
  };
};

/** Slightly different DSP id so Perion accepts a re-POST of the same asset with a new advertiser domain. */
const mintResubmitDspCreativeId = (sellerId, firebaseCreativeId, usedIds) => {
  const base = getCanonicalDspCreativeId(sellerId, firebaseCreativeId);
  const used = usedIds instanceof Set ? usedIds : new Set();
  const first = `${base}_R`;
  if (!used.has(first)) return first;
  let n = 2;
  while (used.has(`${base}_R${n}`)) n += 1;
  return `${base}_R${n}`;
};

const isPerionAlreadyExistsError = (data) => {
  const msg = String(data?.error || data?.message || '').toLowerCase();
  return msg.includes('already exists');
};

/** WxH comparison: spaces, ×, casing, optional "::seller" prefix on creative keys */
const normalizeResolutionFormatToken = (raw) => {
  if (raw == null) return '';
  let s = String(raw).trim().toLowerCase();
  if (!s) return '';
  if (s.includes('::')) s = s.split('::')[0].trim();
  return s.replace(/\u00d7/g, 'x').replace(/\s+/g, '');
};

/**
 * Merge formats from every sign field (not only the first truthy).
 * Returns normalized WxH tokens for matching, plus raw keys for creativesByFormat lookup.
 */
const getSignFormatResolutionMatchData = (sign) => {
  const fields = [
    sign?.ad_formats,
    sign?.preferred_ad_format,
    sign?.Resolution,
    sign?.resolution,
    sign?.slot_dimensions
  ];
  const normalized = new Set();
  const lookupKeys = new Set();
  for (const field of fields) {
    if (field == null || String(field).trim() === '') continue;
    String(field).split(/[,;|]/).forEach((part) => {
      const raw = part.trim();
      if (!raw) return;
      lookupKeys.add(raw);
      lookupKeys.add(raw.toLowerCase());
      const n = normalizeResolutionFormatToken(part);
      if (n) {
        normalized.add(n);
        lookupKeys.add(n);
      }
    });
  }
  if (!normalized.size) {
    normalized.add('1920x1080');
    lookupKeys.add('1920x1080');
  }
  return { normalizedTokens: [...normalized], lookupKeys: [...lookupKeys] };
};

const parseEventTimestamp = (raw) => {
  if (raw == null || raw === '') return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw > 0 && raw < 1e12 ? raw * 1000 : raw;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n < 1e12 ? n * 1000 : n;
  const parsed = Date.parse(String(raw));
  return Number.isFinite(parsed) ? parsed : 0;
};

const mapPulseflowBidStatus = (status) => {
  const s = String(status || '').toLowerCase().trim();
  if (s === 'loss') return 'loss';
  if (s === 'no_bid' || s === 'nobid' || s === 'no-bid') return 'no_bid';
  if (s === 'bid') return 'bid';
  if (s === 'impression') return 'impression';
  if (s === 'win' || s === 'won' || s === 'win_pending_billing' || s === 'billed') return 'win';
  return s || 'bid';
};

const mapPulseflowBidLogRow = (row, idx) => {
  const timestamp = parseEventTimestamp(row?.createdAt || row?.created_at || row?.createdAtMs || row?.ts);
  return {
    id: row?.auctionId || row?.id || `bl-${idx}`,
    type: mapPulseflowBidStatus(row?.status || row?.type),
    timestamp,
    signId: row?.signId || row?.sign_id || row?.openRTBUUID || '',
    auctionId: row?.auctionId || row?.auction_id || '',
    reason: row?.reason || '',
    reasonDescription: row?.reasonDescription || row?.reason || '',
    publisher: row?.publisher || '',
    bidFloor: Number(row?.bidFloor || 0),
    bidPrice: Number(row?.bidPrice || 0),
    spend: Number(row?.actualCost || row?.pacingCost || 0),
    campaignId: row?.campaignId || '',
    deliveryMode: row?.deliveryMode || '',
  };
};

const getCampaignArchiveIds = (campaign) => {
  const id = String(campaign?.id || '').trim();
  return [...new Set([
    id,
    String(campaign?.warmupDspCampaignId || '').trim(),
    id && !id.startsWith('warmup-') ? `warmup-${id}` : '',
    id.startsWith('warmup-') ? id.slice(7) : '',
  ].filter(Boolean))];
};

const CampaignDetail = ({ campaign, onClose }) => {
  const isMobile = useIsMobile();
  const chartHeight = isMobile ? 200 : 300;
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warmupLoading, setWarmupLoading] = useState(false);
  const [showWarmupModal, setShowWarmupModal] = useState(false);
  const [warmupSignKey, setWarmupSignKey] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [editStartDate, setEditStartDate] = useState(campaign?.startDate ? dayjs(campaign.startDate.toDate()).format('YYYY-MM-DD') : '');
  const [editEndDate, setEditEndDate] = useState(campaign?.endDate ? dayjs(campaign.endDate.toDate()).format('YYYY-MM-DD') : '');
  const [rejectReason, setRejectReason] = useState('');
  const [activationBlockReason, setActivationBlockReason] = useState(campaign?.activationBlockedReason || '');
  const [activeTab, setActiveTab] = useState('overview'); // overview, performance, schedule, creatives, signs, rtb
  
  // DSP Analytics State
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [pacingData, setPacingData] = useState(null);
  const [pacingLoading, setPacingLoading] = useState(false);
  const [pacingError, setPacingError] = useState('');
  
  // DSP Configuration State
  const [editBudget, setEditBudget] = useState(campaign?.totalBudget || campaign?.budget || 0);
  const [editMaxCPM, setEditMaxCPM] = useState(campaign?.maxCPM || 0);
  const [editPacingMode, setEditPacingMode] = useState(campaign?.pacingMode || 'even');
  
  // Creative Approval State
  const [flaggedCreatives, setFlaggedCreatives] = useState([]);
  const [creativesLoading, setCreativesLoading] = useState(false);
  /** Hivestack approve flow: editable advertiser domain before POST */
  const [approveHivestackModal, setApproveHivestackModal] = useState(null);
  
  // IAB Category State
  const [iabCategories, setIabCategories] = useState([]);
  const [iabCategoriesLoading, setIabCategoriesLoading] = useState(false);
  const [selectedIabCategories, setSelectedIabCategories] = useState({});
  const [showIabPickerModal, setShowIabPickerModal] = useState(false);
  const [iabSearchTerm, setIabSearchTerm] = useState('');
  const [pendingIabCreativeId, setPendingIabCreativeId] = useState('');
  const [pendingIabCode, setPendingIabCode] = useState('');
  const iabSearchInputRef = useRef(null);

  // Schedule State (weekly time segments grid)
  const [timeSegmentsByDay, setTimeSegmentsByDay] = useState(Array.from({ length: 7 }, () => []));
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Per-sign creatives assignment
  const [creativesByFormat, setCreativesByFormat] = useState({});
  const [assignedCreatives, setAssignedCreatives] = useState({});
  const [showSignCreativesModal, setShowSignCreativesModal] = useState(false);
  const [signCreativesModalSign, setSignCreativesModalSign] = useState(null);
  const [signCreativesSelectedKeys, setSignCreativesSelectedKeys] = useState([]);
  
  // Sign statistics modal (for loss notifications per sign)
  const [showSignStatsModal, setShowSignStatsModal] = useState(false);
  const [signStatsModalSign, setSignStatsModalSign] = useState(null);

  // Creative detail modal (per-creative dates + schedule)
  const [selectedCreativeForDetail, setSelectedCreativeForDetail] = useState(null);
  const [creativeDetailStartDate, setCreativeDetailStartDate] = useState('');
  const [creativeDetailEndDate, setCreativeDetailEndDate] = useState('');
  const [creativeTimeSegmentsByDay, setCreativeTimeSegmentsByDay] = useState(Array.from({ length: 7 }, () => []));
  const [creativeScheduleExpanded, setCreativeScheduleExpanded] = useState(false);
  const [savingCreativeDetail, setSavingCreativeDetail] = useState(false);
  const [dspInstanceInfo, setDspInstanceInfo] = useState(null);
  const [calculatedProfit, setCalculatedProfit] = useState(null);
  const [calculatedMedia, setCalculatedMedia] = useState(null);

  // RTB Events State
  const [rtbEvents, setRtbEvents] = useState([]);
  const [rtbLoading, setRtbLoading] = useState(false);
  const [rtbFilter, setRtbFilter] = useState('all'); // all, won, lost, bid
  const [rtbSearch, setRtbSearch] = useState('');
  const [liveRtbEvents, setLiveRtbEvents] = useState([]);
  const [rtbDateRange, setRtbDateRange] = useState('24h');
  const [rtbStartDate, setRtbStartDate] = useState('');
  const [rtbEndDate, setRtbEndDate] = useState('');
  const [rtbGroupBy, setRtbGroupBy] = useState('hour');
  const [rtbPage, setRtbPage] = useState(1);
  const [rtbEventsPerPage] = useState(50);
  const [rtbSummary, setRtbSummary] = useState(null);
  const [rtbArchiveTotal, setRtbArchiveTotal] = useState(0);
  const [rtbLossReasons, setRtbLossReasons] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (campaign?.userUid) {
      getDoc(doc(db, 'users', campaign.userUid)).then(d => {
        if (d.exists()) setUserInfo(d.data());
      });
    }
    
    // Fetch DSP instance info for tech fee
    console.log('[Profit Debug] campaign.dspInstanceId:', campaign?.dspInstanceId);
    console.log('[Profit Debug] campaign.totalBudget:', campaign?.totalBudget, '| campaign.totalPrice:', campaign?.totalPrice, '| campaign.totalProfit:', campaign?.totalProfit);
    if (campaign?.dspInstanceId) {
      getDoc(doc(db, 'dspInstances', campaign.dspInstanceId)).then(d => {
        console.log('[Profit Debug] DSP instance doc exists:', d.exists());
        if (d.exists()) {
          const instanceData = d.data();
          console.log('[Profit Debug] instanceData.techFee:', instanceData.techFee);
          setDspInstanceInfo(instanceData);
          // Gross = Net × (1 + r)  →  Media (Net) = budget / (1 + r),  Profit = budget - media
          const techFeePercent = instanceData.techFee || 0;
          const r = techFeePercent / 100;
          const budget = campaign.totalBudget || campaign.totalPrice || 0;
          console.log('[Profit Debug] techFeePercent:', techFeePercent, '| r:', r, '| budget:', budget);
          if (budget > 0) {
            const media = budget / (1 + r);
            const profit = budget - media;
            console.log('[Profit Debug] media:', media, '| profit:', profit);
            setCalculatedMedia(media);
            setCalculatedProfit(profit);
          } else {
            console.log('[Profit Debug] Skipped — budget is 0');
            setCalculatedMedia(0);
            setCalculatedProfit(0);
          }
        }
      });
    } else {
      console.log('[Profit Debug] No dspInstanceId — falling back to campaign.totalProfit:', campaign?.totalProfit);
    }
    
    loadIabCategories();
    
    if (isDspCampaign()) {
      fetchCampaignAnalytics();
      fetchPacingData();
    }
    
    if (isDspCampaign() || campaign?.status === 'Pending Vue Approval' || campaign?.status === 'Awaiting Approval' || campaign?.status === 'Draft') {
      fetchFlaggedCreatives();
    }

    initScheduleGridFromCampaign(campaign);
    setActivationBlockReason(campaign?.activationBlockedReason || '');
    fetchAllCampaignCreatives();
    
    // Set up real-time RTB events listener
    let stopRtb = undefined;
    if ((campaign?.dspInstanceId || campaign?.isDspCampaign || campaign?.takeoverDspCampaignId) && campaign?.id) {
      stopRtb = setupRtbEventsListener();
    }
    
    return () => {
      if (typeof stopRtb === 'function') stopRtb();
    };
  }, [campaign]);
  
  const isDspCampaign = () => {
    return campaign?.dspInstanceId || campaign?.isDspCampaign || campaign?.takeoverDspCampaignId;
  };

  const daysOfWeekShort = useMemo(() => (['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']), []);

  const normalizeTimeString = (value) => {
    if (!value || typeof value !== 'string') return '00:00';
    const [rawH, rawM] = value.split(':');
    const hours = Math.min(Math.max(parseInt(rawH || '0', 10), 0), 23);
    const minutes = Math.min(Math.max(parseInt(rawM || '0', 10), 0), 59);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const timeToMinutes = (value) => {
    const [h, m] = normalizeTimeString(value).split(':').map(v => parseInt(v, 10));
    return h * 60 + m;
  };

  const generateTimeSegmentId = () => {
    return `seg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  };

  const deriveActiveDaysHoursFromWeeklySegments = (segments) => {
    const segs = Array.isArray(segments) ? segments : [];
    const activeDaysSet = new Set();
    const activeHoursSet = new Set();
    segs.forEach(seg => {
      const day = Number(seg?.day);
      if (isFinite(day) && day >= 0 && day <= 6) activeDaysSet.add(day);
      const startH = Math.floor(timeToMinutes(seg?.startTime || '00:00') / 60);
      const endH = Math.floor(timeToMinutes(seg?.endTime || '23:59') / 60);
      for (let h = startH; h <= endH; h++) activeHoursSet.add(h);
    });
    return {
      activeDays: Array.from(activeDaysSet).sort((a, b) => a - b),
      activeHours: Array.from(activeHoursSet).sort((a, b) => a - b)
    };
  };

  const initScheduleGridFromCampaign = (camp) => {
    if (!camp) return;
    const grid = Array.from({ length: 7 }, () => []);
    const existing = Array.isArray(camp.weeklyTimeSegments) ? camp.weeklyTimeSegments : [];
    existing.forEach(seg => {
      const day = typeof seg.day === 'number' ? seg.day : 0;
      if (day >= 0 && day < 7) {
        grid[day].push({
          id: seg.id || generateTimeSegmentId(),
          day,
          startTime: normalizeTimeString(seg.startTime || '00:00'),
          endTime: normalizeTimeString(seg.endTime || '23:59')
        });
      }
    });
    setTimeSegmentsByDay(grid);
  };

  const addTimeSegment = (dayIndex) => {
    if (dayIndex < 0 || dayIndex > 6) return;
    const newGrid = [...timeSegmentsByDay];
    newGrid[dayIndex] = [...newGrid[dayIndex], { id: generateTimeSegmentId(), day: dayIndex, startTime: '09:00', endTime: '17:00' }];
    setTimeSegmentsByDay(newGrid);
  };

  const deleteTimeSegment = (dayIndex, segmentId) => {
    const newGrid = [...timeSegmentsByDay];
    newGrid[dayIndex] = newGrid[dayIndex].filter(seg => seg.id !== segmentId);
    setTimeSegmentsByDay(newGrid);
  };

  const updateTimeSegment = (dayIndex, segmentId, field, value) => {
    const newGrid = [...timeSegmentsByDay];
    const segment = newGrid[dayIndex].find(seg => seg.id === segmentId);
    if (segment) {
      segment[field] = normalizeTimeString(value);
    }
    setTimeSegmentsByDay(newGrid);
  };

  const setScheduleTemplate = (type) => {
    const template = Array.from({ length: 7 }, (_, day) => {
      if (type === 'clear') return [];
      if (type === 'weekdays-9-5' && day >= 1 && day <= 5) {
        return [{ id: generateTimeSegmentId(), day, startTime: '09:00', endTime: '17:00' }];
      }
      if (type === 'weekends' && (day === 0 || day === 6)) {
        return [{ id: generateTimeSegmentId(), day, startTime: '00:00', endTime: '23:59' }];
      }
      if (type === 'all') {
        return [{ id: generateTimeSegmentId(), day, startTime: '00:00', endTime: '23:59' }];
      }
      return [];
    });
    setTimeSegmentsByDay(template);
  };

  const copyDaySchedule = (sourceDayIndex) => {
    const source = timeSegmentsByDay[sourceDayIndex] || [];
    if (!source.length) return;
    const clonesFor = (day) =>
      source.map((seg) => ({
        id: generateTimeSegmentId(),
        day,
        startTime: normalizeTimeString(seg.startTime),
        endTime: normalizeTimeString(seg.endTime)
      }));
    setTimeSegmentsByDay(
      timeSegmentsByDay.map((daySegs, dayIndex) => (dayIndex === sourceDayIndex ? daySegs : clonesFor(dayIndex)))
    );
  };

  const persistCampaignPatch = async (patch) => {
    if (!campaign?.id) return;
    try {
      await updateDoc(doc(db, 'campaigns', campaign.id), { ...patch, updatedAt: Timestamp.now() });
      if (campaign.isActivated) {
        await setDoc(doc(db, 'active_dsp_campaigns', campaign.id), { ...patch, updatedAt: Timestamp.now() }, { merge: true });
      }
    } catch (e) {
      console.error('Failed to persist campaign patch:', e);
      throw e;
    }
  };

  const getSellerIdFromSign = (sign) => {
    if (!sign || typeof sign !== 'object') return '';
    return (
      sign.seller_id ||
      sign.sellerId ||
      sign.publisher_id ||
      sign.publisherId ||
      sign.publisher ||
      sign.SellerID ||
      sign.SellerId ||
      sign['Seller ID'] ||
      sign['Seller Id'] ||
      ''
    );
  };

  const getAdunitIdFromSign = (sign) => {
    if (!sign || typeof sign !== 'object') return '';
    return sign.adunit_id || sign.adunitId || sign.adunitIds?.[0] || sign.id || '';
  };

  const getOpenRtbUuidFromSign = (sign) => {
    if (!sign || typeof sign !== 'object') return '';
    return (
      sign.openrtb_uuid ||
      sign.openRtbUuid ||
      sign.openRTBUUID ||
      sign.openRtbUuids?.[0] ||
      sign['OpenRTB UUID'] ||
      ''
    );
  };

  const getSignKey = (sign) =>
    String(
      sign?.openRTBUUID ||
        sign?.openrtb_uuid ||
        sign?.openRtbUuid ||
        sign?.adunit_id ||
        sign?.adunitId ||
        sign?.signId ||
        sign?.id ||
        ''
    ).trim();

  const getSignLabel = (sign) => {
    const assetName = String(sign?.asset_name || sign?.assetName || '').trim();
    if (assetName) return assetName;
    const name = String(sign?.displayString || sign?.name || sign?.unit || sign?.address || sign?.city || 'Sign').trim();
    const unitId = getSignKey(sign);
    return unitId ? `${name} (${unitId})` : name;
  };

  const warmupSignOptions = useMemo(() => {
    const signs = Array.isArray(campaign?.signs) ? campaign.signs : [];
    return signs
      .map((sign) => ({ key: getSignKey(sign), label: getSignLabel(sign), sign }))
      .filter((entry) => entry.key);
  }, [campaign?.signs]);

  const getOxFloorCpm = (sign) => {
    if (!sign || typeof sign !== 'object') return 0;
    const raw =
      sign.ox_floor_cpm ??
      sign.oxFloorCpm ??
      sign['OX Floor CPM'] ??
      sign['OX Floor Cpm'] ??
      sign.ox_floor ??
      0;
    const n = Number(raw);
    return isFinite(n) ? n : 0;
  };

  const buildBidParamsFromSign0 = (camp) => {
    const sign0 = camp?.signs?.[0] || {};
    const floorCpm = getOxFloorCpm(sign0);
    const maxCPM = floorCpm ? floorCpm * 2 : 0;
    const baseCpmCents = floorCpm ? Math.round(floorCpm * 100 * 2) : 0;
    return {
      bidStrategy: 'cpm_target',
      ...(baseCpmCents ? { baseCpmCents } : {}),
      ...(maxCPM ? { maxCPM } : {})
    };
  };

  const buildWarmupWeeklyTimeSegments = () =>
    Array.from({ length: 7 }).map((_, day) => ({
      day,
      startTime: '00:00',
      endTime: '23:59'
    }));

  /** costPerPlay = bidFloor.avg × qty.avgMultiplier ÷ 1000 (from signs.vuebillboards.com). */
  const fetchSignQtyPricing = async (openRtbUuid) => {
    const uuid = String(openRtbUuid || '').trim();
    if (!uuid) return null;

    const res = await fetch(`${SIGNS_QTY_API}/api/qty/${encodeURIComponent(uuid)}`);
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (!res.ok || data?.error) {
      const hint = data?.hint ? ` ${data.hint}` : '';
      throw new Error(String(data?.error || `Sign qty fetch failed (${res.status})`).trim() + hint);
    }

    let costPerPlay = Number(data?.costPerPlay);
    const avgBidFloor = Number(data?.avgBidFloor ?? data?.bidFloor?.avg);
    const avgQtyMultiplier = Number(data?.avgQtyMultiplier ?? data?.qty?.avgMultiplier);

    if (!isFinite(costPerPlay) || costPerPlay <= 0) {
      if (isFinite(avgBidFloor) && isFinite(avgQtyMultiplier)) {
        costPerPlay = (avgBidFloor * avgQtyMultiplier) / 1000;
      }
    }

    if (!isFinite(costPerPlay) || costPerPlay <= 0) return null;

    return {
      costPerPlay,
      avgBidFloor: isFinite(avgBidFloor) ? avgBidFloor : null,
      avgQtyMultiplier: isFinite(avgQtyMultiplier) ? avgQtyMultiplier : null
    };
  };

  /** Warm-up budget = costPerPlay × 2 from signs API for the first targeted sign. */
  const computeWarmupBudgetFromSignsApi = async (targetedSigns = []) => {
    const openRtbUuid = targetedSigns.map(getOpenRtbUuidFromSign).find(Boolean);
    if (!openRtbUuid) {
      throw new Error('No OpenRTB UUID on targeted sign(s).');
    }

    const pricing = await fetchSignQtyPricing(openRtbUuid);
    if (!pricing?.costPerPlay) {
      throw new Error(`Could not compute cost per play for sign ${openRtbUuid}.`);
    }

    const warmBudget = pricing.costPerPlay * 2;
    return {
      warmBudget: Math.round(warmBudget * 100) / 100,
      openRtbUuid,
      ...pricing
    };
  };

  const buildWarmupClientAdvertiserFields = (camp, user) => {
    const { domain } = getAdvertiserDomainDefaultsForApprove(null, camp, user);
    if (!domain) return null;
    const websiteRaw =
      camp?.clientWebsite ||
      camp?.clientwebsite ||
      camp?.website ||
      (domain ? `https://${domain}` : '');
    const clientWebsite = String(websiteRaw || '').trim() || `https://${domain}`;
    return {
      clientWebsite,
      clientDomain: domain,
      client_domain: domain,
      advertiserDomain: domain,
      advertiser_domain: domain
    };
  };

  const fetchDealsForWarmupAutoMatch = async () => {
    const res = await fetch(`${PERION_API_BRIDGE}/api/deals?$top=1000`);
    if (!res.ok) throw new Error(`Deals fetch failed (${res.status})`);
    const data = await res.json();
    const deals = Array.isArray(data) ? data : (data?.data || []);
    return deals.map((d) => {
      const dealId = d?.dealId || d?.deal_id || d?.id || '';
      const seller_id = d?.seller_id || d?.sellerId || '';
      const name = d?.name || dealId || null;
      const floorRaw = d?.floorCpm ?? d?.floor_cpm ?? d?.dealFloorCpm ?? d?.deal_floor_cpm ?? null;
      const floorCpm = floorRaw != null && isFinite(Number(floorRaw)) ? Number(floorRaw) : null;
      const publisher = d?.publisher || d?.publisher_id || null;
      return {
        ...d,
        dealId,
        deal_id: d?.deal_id || dealId,
        seller_id,
        name,
        floorCpm,
        publisher
      };
    }).filter(d => d.dealId);
  };

  const autoMatchDealIdsForWarmupCampaign = async ({ baseCampaign, warmupId }) => {
    const signs = Array.isArray(baseCampaign?.signs) ? baseCampaign.signs : [];
    const sellerIds = [...new Set(signs.map(getSellerIdFromSign).filter(Boolean))];
    if (sellerIds.length === 0) return;

    const deals = await fetchDealsForWarmupAutoMatch();
    const matchingDeals = deals.filter(d => d.seller_id && sellerIds.includes(d.seller_id));
    const matchingDealIds = matchingDeals.map(d => d.dealId);

    const existing = Array.isArray(baseCampaign?.dealIds) ? baseCampaign.dealIds : [];
    const merged = [...new Set([...existing, ...matchingDealIds])];
    const primary = merged[0] || null;

    const existingDeals = Array.isArray(baseCampaign?.deals) ? baseCampaign.deals : [];
    const dealById = new Map();
    for (const d of existingDeals) {
      const id = d?.dealId || d?.deal_id || d?.id;
      if (id) dealById.set(id, d);
    }
    for (const d of matchingDeals) {
      const id = d?.dealId || d?.deal_id || d?.id;
      if (id) dealById.set(id, d);
    }
    const mergedDeals = merged.map(id => dealById.get(id)).filter(Boolean);
    const primaryDeal = primary ? (dealById.get(primary) || null) : null;
    const dealName = primaryDeal?.name || primaryDeal?.dealId || primary || null;
    const dealFloorCpm = primaryDeal?.floorCpm != null ? Number(primaryDeal.floorCpm) : (primaryDeal?.floor_cpm != null ? Number(primaryDeal.floor_cpm) : 0);
    const publisherId = primaryDeal?.publisher || primaryDeal?.publisher_id || null;

    const dealFields = {
      dealIds: merged,
      dealId: primary,
      deals: mergedDeals,
      dealName,
      dealFloorCpm: isFinite(dealFloorCpm) ? dealFloorCpm : 0,
      publisherId,
      updatedAt: new Date()
    };

    await setDoc(doc(db, 'campaigns', warmupId), dealFields, { merge: true });
    await setDoc(doc(db, 'active_dsp_campaigns', warmupId), dealFields, { merge: true });
  };

  const copyCreativesAndDealsForWarmupSign = (base, signKey) => {
    const assigned = base?.assignedCreatives && typeof base.assignedCreatives === 'object' ? base.assignedCreatives : {};
    const filteredAssigned = {};
    Object.entries(assigned).forEach(([k, v]) => {
      if (String(k).trim().toLowerCase() === String(signKey).trim().toLowerCase()) {
        filteredAssigned[k] = v;
      }
    });
    const creativeIds = [
      ...getCampaignCreativeIds(base),
      ...getAssignedCreativeIdsFromCampaign(base)
    ];
    if (!Object.keys(filteredAssigned).length && creativeIds.length) {
      filteredAssigned[signKey] = Array.from(new Set(creativeIds));
    }
    return {
      assignedCreatives: filteredAssigned,
      creativeIndex: base?.creativeIndex && typeof base.creativeIndex === 'object' ? base.creativeIndex : {},
      creativeDetails: base?.creativeDetails && typeof base.creativeDetails === 'object' ? base.creativeDetails : {},
      creativeIds: Array.from(new Set(creativeIds)),
      dealIds: Array.isArray(base?.dealIds) ? base.dealIds : [],
      dealId: base?.dealId || null,
      deals: Array.isArray(base?.deals) ? base.deals : [],
      dealName: base?.dealName || null,
      dealFloorCpm: base?.dealFloorCpm ?? base?.deal_floor_cpm ?? 0,
      publisherId: base?.publisherId || base?.publisher_id || null,
      iabCategory: base?.iabCategory || base?.iab_category || null,
      iabCategories: base?.iabCategories || base?.iab_categories || null
    };
  };

  const handleCreateWarmupCampaign = async () => {
    if (!isDspCampaign()) return;
    const cid = campaign?.id;
    if (!cid) return;

    if (campaign?.isWarmup || campaign?.iswarmup || String(cid).startsWith('warmup-')) {
      alert('Warm-up cannot be created from a warm-up campaign doc.');
      return;
    }
    if (campaign?.warmupDspCampaignId || campaign?.warmupTakeoverId) {
      alert('A warm-up is already linked to this campaign.');
      return;
    }
    if (!warmupSignKey) {
      alert('Select a sign to create the warm-up for.');
      return;
    }

    setWarmupLoading(true);
    try {
      let base = { ...campaign };
      try {
        const campSnap = await getDoc(doc(db, 'campaigns', cid));
        if (campSnap.exists()) base = { ...base, ...campSnap.data() };
      } catch (e) {
        console.warn('Warm-up: could not refresh campaigns doc:', e);
      }
      try {
        const activeSnap = await getDoc(doc(db, 'active_dsp_campaigns', cid));
        if (activeSnap.exists()) base = { ...base, ...activeSnap.data(), id: cid };
      } catch (e) {
        console.warn('Warm-up: could not read active_dsp_campaigns:', e);
      }

      const signs = Array.isArray(base?.signs) ? base.signs : [];
      const selectedSign = signs.find((s) => getSignKey(s) === warmupSignKey);
      if (!selectedSign) {
        alert('Selected sign was not found on this campaign.');
        return;
      }

      const targetedSigns = [{ ...selectedSign, isTargeted: true }];
      const creativeDealFields = copyCreativesAndDealsForWarmupSign(base, warmupSignKey);

      let warmBudget;
      try {
        const pricing = await computeWarmupBudgetFromSignsApi(targetedSigns);
        warmBudget = pricing.warmBudget;
        console.log('[Warm-up] signs qty pricing:', pricing);
      } catch (e) {
        alert(e?.message || 'Could not fetch sign pricing for warm-up budget.');
        return;
      }

      if (!warmBudget || warmBudget <= 0) {
        alert('Could not compute warm-up budget from signs API.');
        return;
      }

      let buyerInfo = userInfo;
      if (!buyerInfo && base?.userUid) {
        try {
          const userSnap = await getDoc(doc(db, 'users', base.userUid));
          if (userSnap.exists()) buyerInfo = userSnap.data();
        } catch (e) {
          console.warn('Warm-up: could not load buyer account for domain:', e);
        }
      }

      const clientAdvertiserFields = buildWarmupClientAdvertiserFields(base, buyerInfo);
      if (!clientAdvertiserFields) {
        alert(
          'Missing advertiser / client domain on this campaign (and buyer account). Add clientWebsite or clientDomain on the campaign before creating a warm-up — the bidder needs advertiser_domain to bid.'
        );
        return;
      }

      const adunitIds = [...new Set(targetedSigns.map(getAdunitIdFromSign).filter(Boolean))];
      const openRtbUuids = [...new Set(targetedSigns.map(getOpenRtbUuidFromSign).filter(Boolean))];

      const startDate = new Date();
      const endDate = dayjs(startDate).add(3, 'day').toDate();
      const weeklyTimeSegments = buildWarmupWeeklyTimeSegments();
      const activeDays = [0, 1, 2, 3, 4, 5, 6];
      const activeHours = Array.from({ length: 24 }).map((_, h) => h);

      const warmupId = `warmup-${cid}`;

      const warmupDoc = {
        ...base,
        id: warmupId,
        isWarmup: true,
        iswarmup: true,
        warmupForCampaignId: cid,
        warmupForTakeoverId: null,
        warmupSignKey,
        name: `warmup-${cid} · ${getSignLabel(selectedSign)}`,
        ...buildBidParamsFromSign0({ ...base, signs: targetedSigns }),
        ...clientAdvertiserFields,
        ...creativeDealFields,
        startDate,
        endDate,
        weeklyTimeSegments,
        activeDays,
        activeHours,
        signs: targetedSigns,
        adunitIds,
        openRtbUuids,
        targeting: { openRtbUuids, adunits: adunitIds },
        totalBudget: warmBudget,
        budget: warmBudget,
        totalPrice: warmBudget,
        pacingMode: 'asap',
        isActivated: true,
        status: 'active',
        updatedAt: new Date(),
        createdAt: new Date(),
        description: (base?.description ? `${base.description} ` : '') + 'Warm-up campaign for player approval.'
      };

      await setDoc(doc(db, 'campaigns', warmupId), warmupDoc, { merge: true });
      await setDoc(doc(db, 'active_dsp_campaigns', warmupId), warmupDoc, { merge: true });

      await updateDoc(doc(db, 'campaigns', cid), {
        warmupDspCampaignId: warmupId,
        warmupSignKey,
        warmupCreatedAt: new Date(),
        updatedAt: Timestamp.now()
      });

      try {
        await autoMatchDealIdsForWarmupCampaign({ baseCampaign: warmupDoc, warmupId });
      } catch (e) {
        console.warn('Warm-up deal auto-match failed:', e);
      }

      setShowWarmupModal(false);
      alert(`Warm-up campaign created: ${warmupId}`);
      window.location.reload();
    } catch (e) {
      console.error('Warm-up campaign creation failed:', e);
      alert(`Warm-up creation failed: ${e.message || 'Unknown error'}`);
    } finally {
      setWarmupLoading(false);
    }
  };

  const handleSaveSchedule = async () => {
    setScheduleSaving(true);
    try {
      const flatSegments = [];
      timeSegmentsByDay.forEach((daySegs, dayIndex) => {
        daySegs.forEach(seg => {
          flatSegments.push({
            id: seg.id || generateTimeSegmentId(),
            day: dayIndex,
            startTime: normalizeTimeString(seg.startTime),
            endTime: normalizeTimeString(seg.endTime)
          });
        });
      });
      const derived = deriveActiveDaysHoursFromWeeklySegments(flatSegments);
      await persistCampaignPatch({
        weeklyTimeSegments: flatSegments,
        activeDays: derived.activeDays,
        activeHours: derived.activeHours
      });

      const takeoverIdCandidates = [
        campaign?.takeoverDspCampaignId,
        campaign?.dspCampaignId,
        campaign?.id
      ].filter(Boolean);
      for (const cand of takeoverIdCandidates) {
        try {
          const snap = await getDoc(doc(db, 'takeovers', cand));
          if (snap.exists()) {
            await updateDoc(doc(db, 'takeovers', cand), { weeklyTimeSegments: flatSegments, activeDays: derived.activeDays, activeHours: derived.activeHours, updatedAt: new Date() });
            const activeSnap = await getDoc(doc(db, 'active_dsp_campaigns', cand));
            if (activeSnap.exists()) {
              await updateDoc(doc(db, 'active_dsp_campaigns', cand), { weeklyTimeSegments: flatSegments, activeDays: derived.activeDays, activeHours: derived.activeHours, updatedAt: new Date() });
            }
            break;
          }
        } catch {}
      }

      alert('Schedule updated successfully');
    } catch (e) {
      alert('Failed to update schedule: ' + e.message);
    } finally {
      setScheduleSaving(false);
    }
  };

  const fetchAllCampaignCreatives = async () => {
    if (!campaign?.creativeDetails) return;
    const formatMap = {};
    try {
      for (const [formatKey, detail] of Object.entries(campaign.creativeDetails)) {
        if (detail.creatives && Array.isArray(detail.creatives)) {
          const loaded = [];
          for (const c of detail.creatives) {
            if (c.id) {
              const snap = await getDoc(doc(db, 'creatives', c.id));
              if (snap.exists()) {
                loaded.push({ id: snap.id, ...snap.data() });
              }
            }
          }
          if (loaded.length > 0) formatMap[formatKey] = loaded;
        }
      }
      setCreativesByFormat(formatMap);
      setAssignedCreatives(campaign.assignedCreatives || {});
    } catch (e) {
      console.error('Failed to load creatives:', e);
    }
  };

  const getSignRowKey = (sign) => {
    return String(sign?.openRTBUUID || sign?.openrtb_uuid || sign?.adunit_id || sign?.signId || sign?.id || '').trim();
  };

  const getCreativeStatus = (creative) => {
    if (!creative) return 'pending';
    
    // Check moderationStatus first (overall status)
    if (creative.moderationStatus) {
      return creative.moderationStatus;
    }
    
    // Check statuses object (per-publisher statuses)
    if (creative.statuses && typeof creative.statuses === 'object') {
      const statuses = Object.values(creative.statuses).map(s => String(s || '').toLowerCase());
      if (statuses.length > 0) {
        if (statuses.every(s => s === 'approved')) return 'approved';
        if (statuses.some(s => s === 'rejected')) return 'rejected';
        if (statuses.some(s => s === 'approved')) return 'partial';
        if (statuses.some(s => s === 'pending')) return 'pending';
      }
    }
    
    // Check legacy status field
    if (creative.status) {
      return creative.status;
    }
    
    // Check approvedByAdmin (legacy)
    if (creative.approvedByAdmin === true) return 'approved';
    if (creative.approvedByAdmin === false) return 'rejected';
    
    return 'pending';
  };

  /** Badge class slug + human label; avoids "Pending" implying admin still needs to POST when creative is already with the exchange. */
  const getCreativeStatusPresentation = (creative) => {
    const raw = getCreativeStatus(creative);
    const key = String(raw || 'pending').toLowerCase().trim() || 'pending';
    if (key === 'pending' && isCreativeSubmittedToPerion(creative)) {
      return { slug: 'exchange-pending', label: 'Exchange review' };
    }
    return {
      slug: key,
      label: typeof raw === 'string' && raw.trim() ? raw : 'Pending',
    };
  };

  const getCreativeUniqKey = (creative) => {
    return String(creative?.id || creative?.dspCreativeId || creative?.originalImageUrl || creative?.croppedImageUrl || '').trim();
  };

  const getAvailableCreativesForSign = (sign) => {
    const { lookupKeys } = getSignFormatResolutionMatchData(sign);
    const merged = [];
    lookupKeys.forEach((key) => {
      const list = creativesByFormat[key] || [];
      merged.push(...list);
    });
    return merged.filter((c, i, arr) => arr.findIndex(x => getCreativeUniqKey(x) === getCreativeUniqKey(c)) === i);
  };

  const getAssignedCreativesForSign = (sign) => {
    const signKey = getSignRowKey(sign);
    const assigned = assignedCreatives[signKey];
    if (Array.isArray(assigned) && assigned.length > 0) return assigned;
    return getAvailableCreativesForSign(sign);
  };

  const openSignCreativesModal = (sign) => {
    if (!sign) return;
    setSignCreativesModalSign(sign);
    const assigned = getAssignedCreativesForSign(sign);
    const keys = assigned.map(c => getCreativeUniqKey(c)).filter(Boolean);
    setSignCreativesSelectedKeys(Array.from(new Set(keys)));
    setShowSignCreativesModal(true);
  };

  const closeSignCreativesModal = () => {
    setShowSignCreativesModal(false);
    setSignCreativesModalSign(null);
    setSignCreativesSelectedKeys([]);
  };

  const saveSignCreativesSelection = async () => {
    if (!campaign?.id || !signCreativesModalSign) return;
    const signKey = getSignRowKey(signCreativesModalSign);
    const available = getAvailableCreativesForSign(signCreativesModalSign);
    const selected = new Set(signCreativesSelectedKeys);
    const chosen = available.filter(c => selected.has(getCreativeUniqKey(c)));
    const newAssigned = { ...assignedCreatives, [signKey]: chosen };
    try {
      await persistCampaignPatch({ assignedCreatives: newAssigned });
      setAssignedCreatives(newAssigned);
      alert('Creatives saved for sign');
      closeSignCreativesModal();
    } catch (e) {
      alert('Failed to save: ' + e.message);
    }
  };

  const resetSignCreativesToDefault = async () => {
    if (!campaign?.id || !signCreativesModalSign) return;
    const signKey = getSignRowKey(signCreativesModalSign);
    const newAssigned = { ...assignedCreatives };
    delete newAssigned[signKey];
    try {
      await persistCampaignPatch({ assignedCreatives: newAssigned });
      setAssignedCreatives(newAssigned);
      alert('Reset to default creatives');
      closeSignCreativesModal();
    } catch (e) {
      alert('Failed to reset: ' + e.message);
    }
  };

  // Sign statistics modal functions
  const openSignStatsModal = (sign) => {
    setSignStatsModalSign(sign);
    setShowSignStatsModal(true);
  };

  const closeSignStatsModal = () => {
    setShowSignStatsModal(false);
    setSignStatsModalSign(null);
  };

  // Calculate RTB statistics for a specific sign
  const getSignRtbStats = (sign) => {
    if (!sign) return null;
    
    const signId = sign.openRTBUUID || sign.openrtb_uuid || sign.adunit_id || sign.signId || sign.id || '';
    const allEvents = [...liveRtbEvents, ...rtbEvents];
    const signEvents = allEvents.filter(e => e.signId === signId);
    
    const stats = {
      total: signEvents.length,
      wins: signEvents.filter(e => e.type === 'win').length,
      losses: signEvents.filter(e => e.type === 'loss').length,
      impressions: signEvents.filter(e => e.type === 'impression').length,
      noBids: signEvents.filter(e => e.type === 'no_bid').length,
      bids: signEvents.filter(e => e.type === 'bid').length,
    };
    
    // Aggregate loss reasons for this sign
    const lossReasonCounts = {};
    signEvents.filter(e => e.type === 'loss').forEach(event => {
      const reason = event.reasonDescription || event.reason || 'Unknown';
      lossReasonCounts[reason] = (lossReasonCounts[reason] || 0) + 1;
    });
    
    stats.lossReasons = Object.entries(lossReasonCounts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    
    return stats;
  };

  const openCreativeDetailModal = (creative) => {
    if (!creative) return;
    setSelectedCreativeForDetail(creative);
    setCreativeDetailStartDate(creative.startDate || '');
    setCreativeDetailEndDate(creative.endDate || '');
    const stored = Array.isArray(creative.weeklyTimeSegments) ? creative.weeklyTimeSegments : [];
    const grid = Array.from({ length: 7 }, () => []);
    stored.forEach(seg => {
      const day = typeof seg.day === 'number' ? seg.day : 0;
      if (day >= 0 && day < 7) {
        grid[day].push({ id: seg.id || generateTimeSegmentId(), day, startTime: normalizeTimeString(seg.startTime || '00:00'), endTime: normalizeTimeString(seg.endTime || '23:59') });
      }
    });
    setCreativeTimeSegmentsByDay(grid);
  };

  const closeCreativeDetailModal = () => {
    setSelectedCreativeForDetail(null);
    setCreativeDetailStartDate('');
    setCreativeDetailEndDate('');
    setCreativeTimeSegmentsByDay(Array.from({ length: 7 }, () => []));
    setSavingCreativeDetail(false);
  };

  const saveCreativeDetailSchedule = async () => {
    if (!selectedCreativeForDetail?.id) return;
    setSavingCreativeDetail(true);
    try {
      const flatSegments = [];
      creativeTimeSegmentsByDay.forEach((daySegs, dayIndex) => {
        daySegs.forEach(seg => {
          flatSegments.push({ id: seg.id || generateTimeSegmentId(), day: dayIndex, startTime: normalizeTimeString(seg.startTime), endTime: normalizeTimeString(seg.endTime) });
        });
      });
      await updateDoc(doc(db, 'creatives', selectedCreativeForDetail.id), {
        startDate: creativeDetailStartDate || null,
        endDate: creativeDetailEndDate || null,
        weeklyTimeSegments: flatSegments.length > 0 ? flatSegments : null,
        updatedAt: new Date().toISOString()
      });
      alert('Creative schedule saved');
      closeCreativeDetailModal();
      fetchAllCampaignCreatives();
    } catch (e) {
      alert('Failed to save creative schedule: ' + e.message);
    } finally {
      setSavingCreativeDetail(false);
    }
  };

  const loadIabCategories = async () => {
    setIabCategoriesLoading(true);
    try {
      const response = await fetch(`${PERION_API_BRIDGE}/api/iab-categories`);
      const data = await response.json();
      setIabCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load IAB categories:', error);
      setIabCategories([]);
    } finally {
      setIabCategoriesLoading(false);
    }
  };
  
  const setupRtbEventsListener = () => {
    if (!campaign?.id) return undefined;

    const campaignIds = new Set(getCampaignArchiveIds(campaign));
    const eventsRef = rtdbRef(rtdb, 'pulseflow/events');
    const eventsQuery = rtdbQuery(eventsRef, limitToLast(1000));

    const handleSnapshot = (snapshot) => {
      if (!snapshot.exists()) {
        setLiveRtbEvents([]);
        return;
      }
      const events = [];
      const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
      snapshot.forEach((childSnapshot) => {
        const val = childSnapshot.val() || {};
        const cid = String(val.campaignId || val.campaign_id || val.data?.campaignId || '');
        if (!campaignIds.has(cid)) return;
        const tsMs = parseEventTimestamp(val.ts ?? val.timestamp ?? val.createdAt ?? val.createdAtMs);
        if (!tsMs || tsMs < thirtyMinutesAgo) return;
        events.push({
          id: childSnapshot.key,
          ...val,
          type: mapPulseflowBidStatus(val.type || val.status),
          timestamp: tsMs,
          signId: val.signId || val.sign_id || val.openRTBUUID || val.openrtb_uuid || '',
          auctionId: val.auctionId || val.auction_id || '',
          reason: val.reason || val.reasonCode || val.reasonDescription || '',
          reasonDescription: val.reasonDescription || val.reason || '',
        });
      });
      events.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
      setLiveRtbEvents(events);
    };

    onValue(eventsQuery, handleSnapshot, (error) => {
      console.error('RTDB listener error:', error);
    });

    return () => off(eventsQuery);
  };

  const fetchHistoricalRtbEvents = async () => {
    if (!campaign?.id) return;
    if (rtbDateRange === 'custom' && (!rtbStartDate || !rtbEndDate)) return;

    setRtbLoading(true);
    try {
      const now = Date.now();
      let startDate = '';
      let endDate = '';
      if (rtbDateRange === '24h') startDate = new Date(now - 24 * 60 * 60 * 1000).toISOString();
      else if (rtbDateRange === '7d') startDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      else if (rtbDateRange === '30d') startDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
      else if (rtbDateRange === 'custom') {
        startDate = rtbStartDate;
        endDate = rtbEndDate;
      }

      const campaignIds = getCampaignArchiveIds(campaign);
      let best = { events: [], total: 0, summary: null, lossReasons: [], sourceId: campaign.id };

      for (const campaignId of campaignIds) {
        const qs = new URLSearchParams({ campaignId, limit: '500', page: '1' });
        if (startDate) qs.set('startDate', startDate);
        if (endDate) qs.set('endDate', endDate);

        const [summaryRes, logsRes, lossRes] = await Promise.all([
          fetch(`${PULSEFLOW_API}/api/archive/bid-logs/summary?${qs.toString()}`),
          fetch(`${PULSEFLOW_API}/api/archive/bid-logs?${qs.toString()}`),
          fetch(`${PULSEFLOW_API}/api/archive/insights/loss-reasons?campaignId=${encodeURIComponent(campaignId)}${startDate ? `&startDate=${encodeURIComponent(startDate.slice(0, 10))}` : ''}${endDate ? `&endDate=${encodeURIComponent(endDate.slice(0, 10))}` : ''}`),
        ]);

        const summary = summaryRes.ok ? await summaryRes.json() : null;
        const logsData = logsRes.ok ? await logsRes.json() : { rows: [], total: 0 };
        const lossData = lossRes.ok ? await lossRes.json() : { reasons: [] };
        const rows = Array.isArray(logsData?.rows) ? logsData.rows : [];
        let events = rows.map(mapPulseflowBidLogRow);
        const total = Number(logsData?.total || summary?.total || events.length);

        // Pull extra pages (newest-first) so charts aren't stuck on a tiny sample.
        const pageSize = Number(logsData?.limit || 500);
        const maxPages = Math.min(4, Math.ceil(total / pageSize) || 1);
        for (let page = 2; page <= maxPages; page += 1) {
          qs.set('page', String(page));
          const extraRes = await fetch(`${PULSEFLOW_API}/api/archive/bid-logs?${qs.toString()}`);
          if (!extraRes.ok) break;
          const extra = await extraRes.json();
          const extraRows = Array.isArray(extra?.rows) ? extra.rows : [];
          if (!extraRows.length) break;
          events = events.concat(extraRows.map((row, idx) => mapPulseflowBidLogRow(row, events.length + idx)));
          if (extraRows.length < pageSize) break;
        }

        if (total > best.total || events.length > best.events.length) {
          best = {
            events,
            total,
            summary,
            lossReasons: Array.isArray(lossData?.reasons) ? lossData.reasons : [],
            sourceId: campaignId,
          };
        }
        if (total > 0) break;
      }

      setRtbEvents(best.events.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)));
      setRtbArchiveTotal(best.total);
      setRtbSummary(best.summary);
      setRtbLossReasons(best.lossReasons);
      setRtbPage(1);
    } catch (error) {
      console.error('Error fetching Pulseflow bid logs:', error);
      setRtbEvents([]);
      setRtbArchiveTotal(0);
      setRtbSummary(null);
      setRtbLossReasons([]);
    } finally {
      setRtbLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== 'rtb' || !campaign?.id) return;
    if (!(campaign.dspInstanceId || campaign.isDspCampaign || campaign.takeoverDspCampaignId)) return;
    fetchHistoricalRtbEvents();
    // fetchHistoricalRtbEvents is recreated each render; date/campaign/tab are the real inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, campaign?.id, rtbDateRange, rtbStartDate, rtbEndDate]);

  const fetchCampaignAnalytics = async () => {
    if (!campaign?.id) return;
    setAnalyticsLoading(true);
    try {
      const qs = `campaignId=${encodeURIComponent(campaign.id)}`;
      const [summaryRes, dailyRes, signsRes] = await Promise.all([
        fetch(`${PULSEFLOW_API}/api/archive/billing/summary?${qs}`),
        fetch(`${PULSEFLOW_API}/api/archive/billing/daily?${qs}`),
        fetch(`${PULSEFLOW_API}/api/archive/billing/signs?${qs}&limit=500`)
      ]);

      if (summaryRes.ok) {
        const [summary, daily, signs] = await Promise.all([
          summaryRes.json(),
          dailyRes.ok ? dailyRes.json() : { rows: [] },
          signsRes.ok ? signsRes.json() : { rows: [] }
        ]);

        const totalSpend = Number(summary.totalBilledDollars || summary.totalSpendDollars || 0);
        const totalImpressions = Number(summary.totalImpressions || 0);
        const totalPlayouts = Number(summary.totalRecords || 0);
        const avgCPM = totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0;

        const byDate = (daily.rows || []).map((row) => ({
          date: row.date,
          playouts: Number(row.records || 0),
          impressions: Number(row.impressions || 0),
          mediaCost: Number(row.mediaDollars || row.spendDollars || 0),
          techFee: Number(row.techFeeDollars || 0),
          totalCost: Number(row.billedDollars || row.spendDollars || 0)
        })).sort((a, b) => String(a.date).localeCompare(String(b.date)));

        const bySign = (signs.rows || [])
          .filter((row) => row.signId)
          .map((row) => ({
            signId: row.signId,
            playouts: Number(row.records || 0),
            impressions: Number(row.impressions || 0),
            mediaCost: Number(row.mediaDollars || row.spendDollars || 0),
            techFee: Number(row.techFeeDollars || 0),
            totalCost: Number(row.billedDollars || row.spendDollars || 0)
          }));

        setAnalyticsData({
          totalSpend,
          totalImpressions,
          totalPlayouts,
          avgCPM,
          mediaCost: Number(summary.totalMediaDollars || summary.totalSpendDollars || 0),
          techFee: Number(summary.totalTechFeeDollars || 0),
          byDate,
          bySign
        });
        return;
      }

      const totals = { playouts: 0, impressions: 0, mediaCost: 0, techFee: 0, totalCost: 0 };
      const byDate = {};
      const bySign = {};
      const billingRef = collection(db, 'billing_records');
      const billingQuery = query(billingRef, where('campaignId', '==', campaign.id));
      const [billingSnap, totalSpendDoc] = await Promise.all([
        getDocs(billingQuery),
        getDoc(doc(db, 'campaigns', campaign.id, 'totalSpend', 'summary'))
      ]);
      
      billingSnap.forEach(docSnap => {
        const rec = docSnap.data();
        const dateKey = rec.date || 'unknown';
        const impressions = Number(rec.impressions || 0);
        const mediaCost = Number(rec.spend ?? rec.baseCost ?? rec.mediaCost ?? 0);
        const techFee = Number(rec.techFee ?? rec.spendAppliedTechFee ?? 0);
        const totalCost = Number(rec.totalAmount ?? rec.spendAppliedTotalAmount ?? (mediaCost + techFee) ?? 0);
        
        totals.playouts += 1;
        totals.impressions += impressions;
        totals.mediaCost += mediaCost;
        totals.techFee += techFee;
        totals.totalCost += totalCost;
        
        if (!byDate[dateKey]) {
          byDate[dateKey] = { date: dateKey, playouts: 0, impressions: 0, mediaCost: 0, techFee: 0, totalCost: 0 };
        }
        byDate[dateKey].playouts += 1;
        byDate[dateKey].impressions += impressions;
        byDate[dateKey].mediaCost += mediaCost;
        byDate[dateKey].techFee += techFee;
        byDate[dateKey].totalCost += totalCost;
        
        const signId = rec.signId || rec.openRTBUUID || 'unknown';
        if (!bySign[signId]) {
          bySign[signId] = { signId, playouts: 0, impressions: 0, mediaCost: 0, techFee: 0, totalCost: 0 };
        }
        bySign[signId].playouts += 1;
        bySign[signId].impressions += impressions;
        bySign[signId].mediaCost += mediaCost;
        bySign[signId].techFee += techFee;
        bySign[signId].totalCost += totalCost;
      });
      
      let finalTotalSpend = totals.totalCost;
      if (totalSpendDoc.exists()) {
        const totalSpendData = totalSpendDoc.data();
        const summarySpend = Number(totalSpendData?.campaignTotalSpent || 0);
        if (summarySpend > 0) {
          finalTotalSpend = summarySpend;
        }
      }
      
      const avgCPM = totals.impressions > 0 ? ((finalTotalSpend / totals.impressions) * 1000) : 0;
      
      setAnalyticsData({
        totalSpend: finalTotalSpend,
        totalImpressions: totals.impressions,
        totalPlayouts: totals.playouts,
        avgCPM: avgCPM,
        byDate: Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date)),
        bySign: Object.values(bySign)
      });
    } catch (e) {
      console.error('Error fetching analytics:', e);
    } finally {
      setAnalyticsLoading(false);
    }
  };
  
  const fetchPacingData = async () => {
    if (!campaign?.id) return;
    setPacingLoading(true);
    setPacingError('');
    try {
      const response = await fetch(`${PULSEFLOW_API}/api/pacing/check/${campaign.id}`);
      const data = await response.json();
      if (data.success) {
        setPacingData(data.data);
      } else {
        setPacingError(data.message || 'Failed to fetch pacing data');
      }
    } catch (err) {
      console.error('Error fetching pacing data:', err);
      setPacingError('Network error');
    } finally {
      setPacingLoading(false);
    }
  };
  
  const fetchFlaggedCreatives = async () => {
    if (!campaign?.id) return;
    setCreativesLoading(true);
    try {
      const creatives = [];
      const isPendingApproval =
        campaign.status === 'Pending Vue Approval' || campaign.status === 'Awaiting Approval' || campaign.status === 'Draft';
      
      if (campaign.creativeDetails) {
        for (const [key, detail] of Object.entries(campaign.creativeDetails)) {
          if (detail.creatives && Array.isArray(detail.creatives)) {
            for (const creative of detail.creatives) {
              if (creative.id) {
                const creativeDoc = await getDoc(doc(db, 'creatives', creative.id));
                if (creativeDoc.exists()) {
                  const creativeData = creativeDoc.data();
                  const onPerion = isCreativeSubmittedToPerion(creativeData);
                  const mod = String(creativeData.moderationStatus || '').toLowerCase();
                  const shouldInclude = onPerion
                    ? false
                    : isPendingApproval
                      ? (!creativeData.approvedByAdmin ||
                          isPreSubmitModerationStatus(creativeData.moderationStatus))
                      : (
                          creativeData.nondsp ||
                          mod === 'pending' ||
                          mod === 'uploaded' ||
                          mod === 'flagged' ||
                          creativeData.status === 'flagged'
                        );
                  
                  if (shouldInclude) {
                    creatives.push({ id: creativeDoc.id, ...creativeData, formatKey: key });
                  }
                }
              }
            }
          }
        }
      }
      
      setFlaggedCreatives(creatives);
      const iabMap = {};
      creatives.forEach(creative => {
        if (creative.iab_categories && creative.iab_categories.length > 0) {
          iabMap[creative.id] = creative.iab_categories[0];
        }
      });
      setSelectedIabCategories(prev => ({...prev, ...iabMap}));
    } catch (e) {
      console.error('Error fetching flagged creatives:', e);
    } finally {
      setCreativesLoading(false);
    }
  };

  /** IAB shown in the picker may come from creative doc only; match that for enable / submit logic. */
  const getEffectiveIabCodeForCreative = (creative) => {
    const fromStateNorm = normalizeStoredIabCode(selectedIabCategories[creative.id]);
    if (fromStateNorm) return fromStateNorm;
    return normalizeStoredIabCode(creative.iab_categories?.[0]);
  };

  const getIabName = (code) => {
    const c = normalizeStoredIabCode(code);
    if (!c) return '';
    const match = (iabCategories || []).find((it) => normalizeStoredIabCode(it?.code) === c);
    return String(match?.name || '').trim() || 'Unknown category';
  };

  const openIabPicker = (creative) => {
    setPendingIabCreativeId(creative.id);
    setPendingIabCode(getEffectiveIabCodeForCreative(creative) || '');
    setIabSearchTerm('');
    setShowIabPickerModal(true);
  };

  const closeIabPicker = () => {
    setShowIabPickerModal(false);
    setPendingIabCreativeId('');
    setPendingIabCode('');
    setIabSearchTerm('');
  };

  const confirmIabPicker = () => {
    const code = normalizeStoredIabCode(pendingIabCode);
    if (!code) {
      alert('Please select an IAB category');
      return;
    }
    if (!pendingIabCreativeId) {
      closeIabPicker();
      return;
    }
    setSelectedIabCategories((prev) => ({ ...prev, [pendingIabCreativeId]: code }));
    closeIabPicker();
  };

  const filteredIabCategories = useMemo(() => {
    const term = String(iabSearchTerm || '').trim().toLowerCase();
    const list = iabCategories || [];
    const filtered = !term
      ? list
      : list.filter((c) => {
          const code = String(c?.code || '').toLowerCase();
          const name = String(c?.name || '').toLowerCase();
          return code.includes(term) || name.includes(term);
        });
    if (!term) return filtered.slice(0, 150);
    return filtered;
  }, [iabCategories, iabSearchTerm]);

  useEffect(() => {
    if (!showIabPickerModal) return;
    const t = setTimeout(() => iabSearchInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [showIabPickerModal]);

  const attachFormatKey = (creative) => {
    if (!creative) return creative;
    if (creative.formatKey) return creative;
    if (!campaign?.creativeDetails) return creative;
    for (const [formatKey, detail] of Object.entries(campaign.creativeDetails)) {
      if (detail?.creatives?.some((c) => c?.id === creative.id)) {
        return { ...creative, formatKey };
      }
    }
    return creative;
  };

  const executeHivestackCreativeApprove = async (creative, advertiserDomain, { forceNewCreativeId = false } = {}) => {
    const selectedIab = getEffectiveIabCodeForCreative(creative);
    if (!selectedIab) {
      alert('Please select an IAB category before approving.');
      return;
    }

    try {
      await updateDoc(doc(db, 'creatives', creative.id), {
        advertiser_domain: advertiserDomain,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to save advertiser domain:', error);
    }

    if (campaign.userUid && !userInfo?.website) {
      try {
        await updateDoc(doc(db, 'users', campaign.userUid), {
          website: advertiserDomain
        });
      } catch (error) {
        console.error('Failed to save website to user account:', error);
      }
    }

    setLoading(true);
    try {
      const creativeResolution = creative.formatKey || creative.resolution || '';
      if (!creativeResolution) {
        alert('Creative is missing resolution/formatKey');
        setLoading(false);
        return;
      }
      // Parse "1080x1920::HS-NA-309" -> resolutionPart "1080x1920", sellerIdFromKey "HS-NA-309"
      const resolutionPart = creativeResolution.includes('::') ? creativeResolution.split('::')[0].trim() : creativeResolution;
      const sellerIdFromKey = creativeResolution.includes('::') ? creativeResolution.split('::').slice(1).join('::').trim() : null;

      const creativeNorm = normalizeResolutionFormatToken(creativeResolution);
      const resolutionPartNorm = normalizeResolutionFormatToken(resolutionPart);
      let matchingSigns = [];
      if (campaign.signs && Array.isArray(campaign.signs)) {
        matchingSigns = campaign.signs.filter((sign) => {
          const { normalizedTokens } = getSignFormatResolutionMatchData(sign);
          return (
            (creativeNorm && normalizedTokens.includes(creativeNorm)) ||
            (resolutionPartNorm && normalizedTokens.includes(resolutionPartNorm))
          );
        });
      }

      let sellerIds = [...new Set(matchingSigns.map(sign => sign['Seller ID'] || sign.seller_id || sign.sellerId).filter(Boolean))];
      if (!sellerIds.length && sellerIdFromKey) {
        sellerIds = [sellerIdFromKey];
      }
      if (!sellerIds.length) {
        alert(matchingSigns.length ? 'No Seller IDs found' : `No signs found for resolution ${creativeResolution}`);
        setLoading(false);
        return;
      }

      let width = creative.width || 1920;
      let height = creative.height || 1080;
      const resolutionForDimensions = resolutionPart.includes('x') ? resolutionPart : creativeResolution;
      if (resolutionForDimensions.includes('x')) {
        const [w, h] = resolutionForDimensions.split('x').map(n => parseInt(n, 10));
        if (w && h) { width = w; height = h; }
      }

      const dspCreativeIds = creative.dspCreativeIds ? [...creative.dspCreativeIds] : [];
      const dspCreativeIdMap = creative.dspCreativeIdMap ? {...creative.dspCreativeIdMap} : {};
      const updatedSellerIds = creative.sellerIds ? [...creative.sellerIds] : [];
      const usedDspIds = getUsedDspCreativeIds(creative);
      const supersededIds = [];

      const inferMediaType = (c) => {
        const url = c?.croppedImageUrl || c?.originalImageUrl || c?.url || '';
        const ext = url.split('.').pop()?.toLowerCase() || '';
        if (['mp4', 'mov', 'webm', 'm4v'].includes(ext)) return 'video';
        return 'image';
      };

      const getFormat = (c) => {
        const url = c?.croppedImageUrl || c?.originalImageUrl || c?.url || '';
        const ext = url.split('.').pop()?.toLowerCase() || '';
        if (ext === 'mp4' || ext === 'm4v') return 'video/mp4';
        if (ext === 'mov') return 'video/quicktime';
        if (ext === 'webm') return 'video/webm';
        if (ext === 'png') return 'image/png';
        if (ext === 'gif') return 'image/gif';
        if (ext === 'webp') return 'image/webp';
        return 'image/jpeg';
      };

      const postCreative = async (payload) => {
        const response = await fetch(`${PERION_API_BRIDGE}/api/creatives`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));
        return { response, data };
      };

      let submittedCount = 0;
      for (const sellerId of sellerIds) {
        let dspCreativeId = creative.dspCreativeIdMap?.[sellerId];

        if (forceNewCreativeId) {
          const previousId = String(dspCreativeId || '').trim();
          if (previousId) supersededIds.push(previousId);
          dspCreativeId = mintResubmitDspCreativeId(sellerId, creative.id, usedDspIds);
          usedDspIds.add(dspCreativeId);
          console.log('Generated resubmit DSP creative ID:', dspCreativeId);
        } else if (!dspCreativeId || !dspCreativeId.startsWith('VUE-')) {
          // Format: VUE-CL-{sellerId}_{creativeId}
          dspCreativeId = getCanonicalDspCreativeId(sellerId, creative.id);
          console.log('Generated new DSP creative ID:', dspCreativeId);
        }

        const payload = {
          creative_id: dspCreativeId,
          name: dspCreativeId,
          advertiser_domain: advertiserDomain,
          publisher_id: sellerId,
          media_files: [{
            url: creative.croppedImageUrl || creative.originalImageUrl || creative.url,
            type: inferMediaType(creative),
            format: getFormat(creative),
            width: width,
            height: height
          }],
          iab_categories: [selectedIab]
        };

        let { response, data } = await postCreative(payload);

        if (!response.ok && isPerionAlreadyExistsError(data)) {
          if (forceNewCreativeId) {
            let posted = false;
            for (let attempt = 0; attempt < 5 && !posted; attempt++) {
              dspCreativeId = mintResubmitDspCreativeId(sellerId, creative.id, usedDspIds);
              usedDspIds.add(dspCreativeId);
              payload.creative_id = dspCreativeId;
              payload.name = dspCreativeId;
              ({ response, data } = await postCreative(payload));
              if (response.ok) {
                posted = true;
              } else if (!isPerionAlreadyExistsError(data)) {
                break;
              }
            }
          } else {
            // Creative already registered in Perion under this ID — treat as linked; do not mint a new _R id.
            console.warn(`Creative ID ${dspCreativeId} already exists for ${sellerId}; reusing existing DSP creative.`);
            response = { ok: true };
          }
        }

        if (!response.ok) {
          console.error(`Failed to submit to ${sellerId}:`, data);
          continue;
        }

        if (!dspCreativeIds.includes(dspCreativeId)) dspCreativeIds.push(dspCreativeId);
        dspCreativeIdMap[sellerId] = dspCreativeId;
        if (!updatedSellerIds.includes(sellerId)) updatedSellerIds.push(sellerId);
        submittedCount++;
      }

      const nowIso = new Date().toISOString();
      // Build the update object with all necessary fields
      const creativeUpdate = {
        moderationStatus: 'submitted',
        lastSubmittedAt: nowIso,
        dspSubmittedAt: nowIso,
        approvedByAdmin: true,
        approvedByAdminAt: nowIso,
        updatedAt: nowIso,
        nondsp: false,
        iab_categories: [selectedIab],
        advertiser_domain: advertiserDomain,
        dspCreativeIdMap: dspCreativeIdMap,
        sellerIds: updatedSellerIds,
        lastApprovalCheck: nowIso,
        dspApprovalState: 1, // 1 = pending (waiting for approval)
        // Build statuses object for each seller
        statuses: sellerIds.reduce((acc, sellerId) => {
          acc[sellerId] = 'submitted';
          return acc;
        }, {})
      };

      if (forceNewCreativeId) {
        const previousDomain = String(creative.advertiser_domain || creative.clientDomain || creative.domain || '').trim();
        if (previousDomain && previousDomain !== advertiserDomain) {
          creativeUpdate.previousAdvertiserDomain = previousDomain;
        }
      }

      // Set the primary dspCreativeId (legacy single field)
      if (forceNewCreativeId) {
        const latestMapped = sellerIds.map((s) => dspCreativeIdMap[s]).filter(Boolean);
        if (latestMapped.length > 0) creativeUpdate.dspCreativeId = latestMapped[latestMapped.length - 1];
      } else if (dspCreativeIds.length > 0) {
        creativeUpdate.dspCreativeId = dspCreativeIds[0];
      }

      await updateDoc(doc(db, 'creatives', creative.id), creativeUpdate);

      // Add all DSP creative IDs to the array using arrayUnion (prevents duplicates)
      if (dspCreativeIds.length > 0) {
        await updateDoc(doc(db, 'creatives', creative.id), {
          dspCreativeIds: arrayUnion(...dspCreativeIds)
        });
      }

      if (forceNewCreativeId && supersededIds.length > 0) {
        await updateDoc(doc(db, 'creatives', creative.id), {
          supersededDspCreativeIds: arrayUnion(...supersededIds)
        });
      }

      if (submittedCount > 0) {
        alert(
          forceNewCreativeId
            ? `Creative resubmitted to ${submittedCount} seller(s) with domain ${advertiserDomain} and a new DSP creative ID.`
            : `Creative approved and submitted to ${submittedCount} seller(s)!`
        );
        fetchFlaggedCreatives();
        fetchAllCampaignCreatives();
      } else {
        alert('No sellers were successfully submitted');
      }
    } catch (error) {
      console.error('Failed to approve creative:', error);
      alert('Failed to submit creative: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveCreative = (creative) => {
    if (isCreativeSubmittedToPerion(creative)) {
      alert('This creative is already submitted to Perion (DSP). Use Resubmit domain if you need to send the same asset with a new advertiser domain.');
      return;
    }
    const selectedIab = getEffectiveIabCodeForCreative(creative);
    if (!selectedIab) {
      alert('Please select an IAB category before approving.');
      return;
    }
    const { domain, sourceLabel } = getAdvertiserDomainDefaultsForApprove(creative, campaign, userInfo);
    setApproveHivestackModal({
      creative: attachFormatKey(creative),
      domainDraft: domain,
      sourceLabel,
      isResubmit: false,
    });
  };

  const handleResubmitCreative = (creative) => {
    const withKey = attachFormatKey(creative);
    const selectedIab = getEffectiveIabCodeForCreative(withKey);
    if (!selectedIab) {
      alert('Please select an IAB category before resubmitting.');
      openIabPicker(withKey);
      return;
    }
    const { domain, sourceLabel } = getAdvertiserDomainDefaultsForApprove(withKey, campaign, userInfo);
    closeCreativeDetailModal();
    setApproveHivestackModal({
      creative: withKey,
      domainDraft: domain,
      sourceLabel,
      isResubmit: true,
    });
  };

  const handleConfirmApproveHivestack = async () => {
    if (!approveHivestackModal) return;
    const advertiserDomain = cleanAdvertiserDomainInput(approveHivestackModal.domainDraft);
    if (!advertiserDomain) {
      alert('Advertiser domain is required.');
      return;
    }
    const creative = approveHivestackModal.creative;
    const forceNewCreativeId = !!approveHivestackModal.isResubmit;
    setApproveHivestackModal(null);
    await executeHivestackCreativeApprove(creative, advertiserDomain, { forceNewCreativeId });
  };
  
  const handleRejectCreative = async (creative) => {
    if (!window.confirm('Reject this creative?')) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'creatives', creative.id), {
        moderationStatus: 'rejected',
        rejectedByAdmin: true,
        rejectedByAdminAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      alert('Creative rejected');
      fetchFlaggedCreatives();
    } catch (error) {
      console.error('Failed to reject creative:', error);
      alert('Failed to reject creative');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveActivationBlock = async (blocked) => {
    const trimmedReason = activationBlockReason.trim();
    if (blocked && !trimmedReason) {
      alert('Enter a reason to block activation.');
      return;
    }
    if (!window.confirm(blocked ? 'Block activation for this campaign?' : 'Clear activation block?')) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'campaigns', campaign.id), {
        activationBlocked: blocked,
        activationBlockedReason: blocked ? trimmedReason : null,
        updatedAt: Timestamp.now()
      });
      alert(blocked ? 'Activation blocked.' : 'Activation block cleared.');
      window.location.reload();
    } catch (error) {
      console.error('Error updating activation block:', error);
      alert('Failed to update activation block');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCampaignActivation = async () => {
    const currentlyActive = campaign?.isActivated || false;
    if (!currentlyActive && campaign?.activationBlocked) {
      alert(campaign.activationBlockedReason || 'Activation is blocked for this campaign.');
      return;
    }

    const signs = Array.isArray(campaign?.signs) ? campaign.signs : [];
    const targetedSigns = signs.filter((s) => s?.isTargeted !== false);
    const openRtbUuids = [...new Set(targetedSigns.map(getOpenRtbUuidFromSign).filter(Boolean))];
    const adunitIds = [...new Set(targetedSigns.map(getAdunitIdFromSign).filter(Boolean))];

    if (!currentlyActive) {
      if (Number(campaign?.maxCPM || 0) <= 0) {
        alert('Set Max CPM greater than $0.00 before activating this campaign.');
        return;
      }
      if (!openRtbUuids.length) {
        alert('This campaign has no targeted signs with an OpenRTB UUID. Add or unpause a sign before activating.');
        return;
      }
    }

    const action = currentlyActive ? 'deactivate' : 'activate';
    if (!window.confirm(`${action} this campaign?`)) return;
    setLoading(true);
    try {
      const patch = {
        isActivated: !currentlyActive,
        status: !currentlyActive ? 'active' : 'paused',
        openRtbUuids,
        adunitIds,
        targeting: { openRtbUuids, adunits: adunitIds },
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(db, 'campaigns', campaign.id), patch);

      if (!currentlyActive) {
        await setDoc(
          doc(db, 'active_dsp_campaigns', campaign.id),
          {
            ...campaign,
            ...patch,
            assignedCreatives: campaign.assignedCreatives || {},
            creativeIndex: campaign.creativeIndex || {},
            creativeDetails: campaign.creativeDetails || {},
            weeklyTimeSegments: campaign.weeklyTimeSegments || [],
            activeDays: campaign.activeDays || [],
            activeHours: campaign.activeHours || [],
            maxCPM: campaign.maxCPM,
            baseCpmCents: campaign.baseCpmCents,
            totalBudget: campaign.totalBudget || campaign.budget,
            dspInstanceId: campaign.dspInstanceId || null
          },
          { merge: true }
        );
      } else {
        await deleteDoc(doc(db, 'active_dsp_campaigns', campaign.id));
      }

      const dspActivationUserId = String(campaign.userUid || campaign.userId || campaign.createdBy || '').trim();
      const activationHeaders = { 'Content-Type': 'application/json' };
      try {
        const token = await auth.currentUser?.getIdToken();
        if (token) activationHeaders.Authorization = `Bearer ${token}`;
      } catch (e) {
        console.warn('Could not attach auth token to activation request:', e);
      }

      const response = await fetch(`${ANALYTICS_API}/api/dsp/campaign-activation`, {
        method: 'POST',
        headers: activationHeaders,
        body: JSON.stringify({
          userId: dspActivationUserId,
          userUid: dspActivationUserId,
          campaignId: campaign.id,
          id: campaign.id,
          campaignName: campaign.name,
          startDate: campaign.startDate,
          endDate: campaign.endDate,
          activated: !currentlyActive,
          openRtbUuids,
          adunitIds,
          billTerms: campaign.billTerms || 'pre_pay',
          targeting: { openRtbUuids, adunits: adunitIds },
          signGroups: campaign.signGroups || {},
          clientId: campaign.clientId || null,
          dspInstanceId: campaign.dspInstanceId || null,
          weeklyTimeSegments: campaign.weeklyTimeSegments || [],
          activeDays: campaign.activeDays || [],
          activeHours: campaign.activeHours || []
        })
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        console.warn('Activation API error:', response.status, errText);
      }

      alert(`Campaign ${!currentlyActive ? 'activated' : 'deactivated'}`);
      window.location.reload();
    } catch (error) {
      console.error('Error toggling campaign:', error);
      alert('Failed to update campaign');
    } finally {
      setLoading(false);
    }
  };
  
  const handleUpdateBudget = async () => {
    setLoading(true);
    try {
      const baseCpmCents = Math.max(0, Math.round(editMaxCPM * 100));
      await updateDoc(doc(db, 'campaigns', campaign.id), {
        totalBudget: editBudget,
        budget: editBudget,
        maxCPM: editMaxCPM,
        baseCpmCents: baseCpmCents,
        pacingMode: editPacingMode,
        updatedAt: Timestamp.now()
      });
      
      if (campaign.isActivated) {
        await setDoc(doc(db, 'active_dsp_campaigns', campaign.id), {
          totalBudget: editBudget,
          budget: editBudget,
          maxCPM: editMaxCPM,
          baseCpmCents: baseCpmCents,
          pacingMode: editPacingMode,
          updatedAt: Timestamp.now()
        }, { merge: true });
      }
      
      alert('Budget updated');
      window.location.reload();
    } catch (error) {
      console.error('Error updating budget:', error);
      alert('Failed to update budget');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSignTargeting = async (signId, targeted) => {
    setLoading(true);
    try {
      const signs = [...(campaign.signs || [])];
      const sign = signs.find(s => {
        const sId = s.openRTBUUID || s.openrtb_uuid || s.adunit_id || s.signId || s.id || '';
        return sId === signId;
      });
      if (sign) sign.isTargeted = targeted;
      
      const openRtbUuids = Array.from(new Set(signs.filter(s => s.isTargeted !== false).map(s => s.openRTBUUID || s.openrtb_uuid || '').filter(Boolean)));
      
      await updateDoc(doc(db, 'campaigns', campaign.id), { signs, openRtbUuids, updatedAt: Timestamp.now() });
      
      if (campaign.isActivated) {
        await setDoc(doc(db, 'active_dsp_campaigns', campaign.id), { signs, openRtbUuids, targeting: { openRtbUuids }, updatedAt: Timestamp.now() }, { merge: true });
      }
      
      alert(`Sign ${targeted ? 'targeted' : 'untargeted'}`);
      window.location.reload();
    } catch (error) {
      console.error('Error updating sign:', error);
      alert('Failed to update sign');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'campaigns', campaign.id), { 
        status: newStatus,
        rejectReason: newStatus === 'Rejected' ? rejectReason : null
      });
      alert(`Status updated to ${newStatus}`);
    } catch (e) {
      alert('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleCapturePayment = async () => {
    if (!window.confirm(`Capture payment of ${formatCurrency(campaign.totalPrice)}?`)) return;
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await fetch(`${API_BASE}/api/capture-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId: campaign.paymentTransactionId, amount: campaign.totalPrice })
      });
      const data = await response.json();
      if (data.success) {
        await updateDoc(doc(db, 'campaigns', campaign.id), { 
          paymentStatus: 'captured',
          captureTransactionId: data.transactionId || null,
          captureTimestamp: new Date(),
          status: 'Active'
        });
        alert('Payment captured! Campaign is now Active.');
      } else {
        setPaymentError(data.message || 'Capture failed');
      }
    } catch (err) {
      setPaymentError('Network error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleVoidTransaction = async () => {
    if (!window.confirm('Void this authorization?')) return;
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await fetch(`${API_BASE}/api/void-transaction`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId: campaign.paymentTransactionId, campaignId: campaign.id })
      });
      const data = await response.json();
      if (data.success) {
        await updateDoc(doc(db, 'campaigns', campaign.id), { 
          paymentStatus: 'voided',
          status: 'Rejected',
          rejectReason: 'Payment authorization voided by admin'
        });
        alert('Transaction voided');
      } else {
        setPaymentError(data.message || 'Void failed');
      }
    } catch (err) {
      setPaymentError('Network error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleRefundPayment = async () => {
    const amount = prompt(`Refund amount (max ${formatCurrency(campaign.totalPrice)}):`, campaign.totalPrice);
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0 || parseFloat(amount) > campaign.totalPrice) {
      alert('Invalid refund amount');
      return;
    }
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const response = await fetch(`${API_BASE}/api/refund-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId: campaign.paymentTransactionId || campaign.captureTransactionId, amount: parseFloat(amount), campaignId: campaign.id })
      });
      const data = await response.json();
      if (data.success) {
        await updateDoc(doc(db, 'campaigns', campaign.id), { 
          paymentStatus: 'refunded',
          refundAmount: parseFloat(amount),
          refundTimestamp: new Date()
        });
        alert(`Refunded ${formatCurrency(amount)}`);
      } else {
        setPaymentError(data.message || 'Refund failed');
      }
    } catch (err) {
      setPaymentError('Network error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleSaveDates = async () => {
    setLoading(true);
    try {
      const startDate = new Date(editStartDate);
      const endDate = new Date(editEndDate);
      await updateDoc(doc(db, 'campaigns', campaign.id), { startDate, endDate });
      
      const takeoverIdCandidates = [campaign?.takeoverDspCampaignId, campaign?.dspCampaignId, campaign?.id].filter(Boolean);
      for (const cand of takeoverIdCandidates) {
        try {
          const snap = await getDoc(doc(db, 'takeovers', cand));
          if (snap.exists()) {
            await updateDoc(doc(db, 'takeovers', cand), { startDate, endDate, updatedAt: new Date() });
            const activeSnap = await getDoc(doc(db, 'active_dsp_campaigns', cand));
            if (activeSnap.exists()) {
              await updateDoc(doc(db, 'active_dsp_campaigns', cand), { startDate, endDate, updatedAt: new Date() });
            }
            break;
          }
        } catch {}
      }
      alert('Dates updated');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => Number(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const isVideo = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.webm') || lower.includes('.m4v') || lower.includes('video') || lower.includes('blob:');
  };

  const getHealthClass = (health) => {
    if (!health) return 'health-unknown';
    if (health < 50) return 'health-critical';
    if (health < 90) return 'health-behind';
    if (health <= 110) return 'health-good';
    return 'health-ahead';
  };

  // Memoize RTB chart data so it recalculates when events or filters change (MUST be before any early return)
  const rtbChartData = useMemo(() => {
    const allEvents = [...liveRtbEvents, ...rtbEvents];
    
    // Live RTB Events Summary
    const liveCounts = {
      win: liveRtbEvents.filter(e => e.type === 'win').length,
      impression: liveRtbEvents.filter(e => e.type === 'impression').length,
      loss: liveRtbEvents.filter(e => e.type === 'loss').length,
      no_bid: liveRtbEvents.filter(e => e.type === 'no_bid').length,
      bid: liveRtbEvents.filter(e => e.type === 'bid').length
    };
    const liveTotal = Object.values(liveCounts).reduce((a, b) => a + b, 0);
    const livePieData = [
      { name: 'Wins', value: liveCounts.win, color: '#22c55e' },
      { name: 'Impressions', value: liveCounts.impression, color: '#10b981' },
      { name: 'Losses', value: liveCounts.loss, color: '#ef4444' },
      { name: 'No Bids', value: liveCounts.no_bid, color: '#f59e0b' },
      { name: 'Bids', value: liveCounts.bid, color: '#6b7280' }
    ].filter(d => d.value > 0);
    
    // Group by time period for time-series chart
    const timeData = {};
    const dateFormat = rtbGroupBy === 'day' ? 'MMM D' : 'MMM D, HH:00';
    allEvents.forEach(event => {
      if (!event.timestamp) return;
      const timeKey = dayjs(event.timestamp).format(dateFormat);
      if (!timeData[timeKey]) {
        timeData[timeKey] = { time: timeKey, win: 0, loss: 0, impression: 0, no_bid: 0, bid: 0 };
      }
      const type = event.type || 'unknown';
      if (timeData[timeKey][type] !== undefined) {
        timeData[timeKey][type]++;
      }
    });
    
    const timeChartData = Object.values(timeData).sort((a, b) => {
      return dayjs(a.time, dateFormat).unix() - dayjs(b.time, dateFormat).unix();
    });
    
    const lossReasonData = rtbLossReasons.length > 0
      ? rtbLossReasons.slice(0, 10).map((row) => ({
          name: row.reason || row.name || 'Unknown',
          value: Number(row.count || row.value || 0),
        }))
      : Object.entries(allEvents.filter(e => e.type === 'loss').reduce((acc, event) => {
          const reason = event.reasonDescription || event.reason || 'Unknown';
          acc[reason] = (acc[reason] || 0) + 1;
          return acc;
        }, {}))
          .map(([name, value]) => ({ name, value }))
          .sort((a, b) => b.value - a.value)
          .slice(0, 10);
    
    return { liveCounts, liveTotal, livePieData, timeChartData, lossReasonData };
  }, [liveRtbEvents, rtbEvents, rtbGroupBy, rtbLossReasons]);

  if (!campaign) return null;

  const customDesign = getCustomDesignAddon(campaign);
  const photoOpAddon = getPhotoOpAddon(campaign);
  const photoOpFundPayment = photoOpAddon.payment;
  const photoOpFundConsumed = photoOpFundPayment?.consumed === true;
  const photoOpFundPaidAt = formatCampaignTimestamp(photoOpFundPayment?.paidAt);
  const photoOpPendingSavedAt = formatCampaignTimestamp(photoOpAddon.pending?.savedAt);

  // Chart data prep
  const impressionsTrendData = analyticsData?.byDate || [];
  const spendTrendData = analyticsData?.byDate || [];
  const signPerformanceData = (analyticsData?.bySign || []).slice(0, 10);

  return (
    <ReactModal
      isOpen={!!campaign}
      onRequestClose={onClose}
      ariaHideApp={false}
      className="campaign-detail-modal"
      overlayClassName="campaign-detail-overlay"
      style={{
        overlay: { inset: 0 },
        content: {
          inset: 'unset',
          position: 'relative',
          border: 'none',
          padding: 0,
          overflow: 'hidden',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div className="campaign-detail-header">
        <div className="campaign-detail-header-left">
          <h1 className="campaign-detail-title">{campaign.name}</h1>
          <div className="campaign-detail-meta">
            <span className="campaign-id-display">
              <strong>ID:</strong> {campaign.id}
            </span>
          </div>
          <div className="campaign-detail-badges">
            <span className={`badge badge-${campaign.paymentStatus === 'captured' ? 'success' : campaign.paymentStatus === 'authorized' ? 'warning' : 'default'}`}>
              {campaign.paymentStatus === 'captured' && <FaCheckCircle />}
              {campaign.paymentStatus || 'No Payment'}
            </span>
            <span className={`badge badge-${campaign.status === 'Active' ? 'success' : campaign.status === 'Rejected' ? 'danger' : 'default'}`}>
              {campaign.status}
            </span>
            {campaign.isActivated && <span className="badge badge-primary"><FaPlay /> Live</span>}
            {campaign.activationBlocked && (
              <span className="badge badge-danger" title={campaign.activationBlockedReason || undefined}>
                <FaExclamationTriangle /> {isMobile ? 'Blocked' : 'Activation Blocked'}
              </span>
            )}
            {customDesign.utilized && (
              <span className="badge badge-primary" title="Custom Design Package selected at checkout">
                <FaCheckCircle /> {isMobile ? 'Design' : 'Custom Design'}
              </span>
            )}
            {photoOpAddon.selected && (
              <span className="badge badge-primary" title="Photo Op add-on selected at checkout">
                <FaCheckCircle /> Photo Op
              </span>
            )}
            {photoOpAddon.hasSessionFund && (
              <span
                className={`badge ${photoOpFundConsumed ? 'badge-success' : 'badge-warning'}`}
                title={photoOpFundConsumed ? 'Photo op session fund consumed' : 'Photo op session fund pending or paid'}
              >
                {photoOpFundConsumed ? <FaCheckCircle /> : <FaCreditCard />}
                {isMobile ? 'PO Fund' : 'Session Fund'}
              </span>
            )}
          </div>
        </div>
        <div className="campaign-detail-header-actions">
          <button 
            type="button"
            className="btn btn-secondary"
            onClick={() => window.open(`https://app.vuebillboards.com/campaign/${campaign.id}`, '_blank')}
            title="View Customer Portal"
          >
            <FaExternalLinkAlt /> {isMobile ? 'Portal' : 'Customer View'}
          </button>
          <button type="button" className="campaign-detail-close" onClick={onClose} aria-label="Close campaign details">
            <FaClose />
          </button>
        </div>
      </div>

      <div className="campaign-detail-tabs">
        <button type="button" className={`tab ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
          Overview
        </button>
        {isDspCampaign() && (
          <button type="button" className={`tab ${activeTab === 'performance' ? 'active' : ''}`} onClick={() => setActiveTab('performance')}>
            <FaChartLine /> {isMobile ? 'Perf' : 'Performance'}
          </button>
        )}
        <button type="button" className={`tab ${activeTab === 'schedule' ? 'active' : ''}`} onClick={() => setActiveTab('schedule')}>
          {isMobile ? 'Hours' : 'Schedule'}
        </button>
        <button type="button" className={`tab ${activeTab === 'creatives' ? 'active' : ''}`} onClick={() => setActiveTab('creatives')}>
          {isMobile ? 'Ads' : 'Creatives'}
        </button>
        <button type="button" className={`tab ${activeTab === 'signs' ? 'active' : ''}`} onClick={() => setActiveTab('signs')}>
          Signs ({campaign.signs?.length || 0})
        </button>
        {isDspCampaign() && (
          <button type="button" className={`tab ${activeTab === 'rtb' ? 'active' : ''}`} onClick={() => setActiveTab('rtb')}>
            <FaBolt /> RTB
          </button>
        )}
      </div>

      <div className="campaign-detail-body">
        {activeTab === 'overview' && (
          <div className="tab-content">
            <div className="detail-grid">
              {/* Financial Card */}
              <div className="detail-card card-highlight-green">
                <div className="card-header">
                  <h3>Financial Overview</h3>
                  <FaCreditCard className="card-icon" />
                </div>
                <div className="metrics-grid">
                  <div className="metric">
                    <span className="metric-label">Total Budget</span>
                    <span className="metric-value metric-value-large">{formatCurrency(campaign.totalPrice || campaign.totalBudget)}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Est. Profit</span>
                    <span className="metric-value metric-value-large text-success">
                      {formatCurrency(calculatedProfit !== null ? calculatedProfit : campaign.totalProfit)}
                    </span>
                    {dspInstanceInfo?.techFee > 0 && (
                      <small className="metric-note">Tech Fee: {dspInstanceInfo.techFee}%</small>
                    )}
                  </div>
                  <div className="metric">
                    <span className="metric-label">Media Cost</span>
                    <span className="metric-value">{formatCurrency(calculatedMedia !== null ? calculatedMedia : campaign.totalMediaCost)}</span>
                  </div>
                {isDspCampaign() && (analyticsData || pacingData) && (
                    <div className="metric">
                      <span className="metric-label">Total Spent</span>
                      <span className="metric-value">
                        {formatCurrency(
                          analyticsData?.totalSpend ||
                            pacingData?.plan?.budgets?.spentTotal ||
                            pacingData?.plan?.budgets?.spentToday ||
                            0
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {campaign.paymentTransactionId && (
                  <div className="payment-details">
                    <div className="payment-info">
                      <small>Transaction ID</small>
                      <code>{campaign.paymentTransactionId}</code>
                    </div>
                    {paymentError && <div className="alert alert-danger"><FaTimesCircle /> {paymentError}</div>}
                    {campaign.paymentStatus === 'captured' && (
                      <div className="payment-actions">
                        <button className="btn btn-sm btn-danger" onClick={handleRefundPayment} disabled={paymentLoading}>
                          {paymentLoading ? <FaSpinner className="fa-spin" /> : 'Issue Refund'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Customer Info */}
              <div className="detail-card">
                <div className="card-header">
                  <h3>Customer</h3>
                  <FaBuilding className="card-icon" />
                </div>
                {userInfo ? (
                  <div className="customer-info">
                    <div className="customer-avatar">
                      {(userInfo.name || userInfo.email || '?')[0].toUpperCase()}
                    </div>
                    <div className="customer-details">
                      <div className="customer-name">{userInfo.name || '—'}</div>
                      <div className="customer-email">{userInfo.email}</div>
                      {userInfo.companyName && <div className="customer-company"><FaBuilding /> {userInfo.companyName}</div>}
                      {userInfo.phoneNumber && <div className="customer-phone"><FaPhone /> {userInfo.phoneNumber}</div>}
                    </div>
                  </div>
                ) : <div className="loading-text">Loading...</div>}
              </div>

              <div className={`detail-card card-highlight-blue${customDesign.hasDetails ? ' card-full-width' : ''}`}>
                  <div className="card-header">
                    <h3>Checkout Add-ons</h3>
                    <FaCheckCircle className="card-icon" />
                  </div>
                  <div className="addon-list">
                    <div className={`addon-row ${customDesign.utilized ? 'addon-row-on' : ''}`}>
                      <span className={`addon-check ${customDesign.utilized ? 'on' : ''}`}>
                        {customDesign.utilized ? <FaCheckCircle /> : <FaTimesCircle />}
                      </span>
                      <div className="addon-copy">
                        <div className="addon-title">Custom Design Package</div>
                        <div className="addon-sub">
                          {customDesign.utilized ? 'Selected' : 'Not added'}
                          {customDesign.utilized && customDesign.price > 0 ? ` · ${formatCurrency(customDesign.price)}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className={`addon-row ${photoOpAddon.selected ? 'addon-row-on' : ''}`}>
                      <span className={`addon-check ${photoOpAddon.selected ? 'on' : ''}`}>
                        {photoOpAddon.selected ? <FaCheckCircle /> : <FaTimesCircle />}
                      </span>
                      <div className="addon-copy">
                        <div className="addon-title">Photo Op</div>
                        <div className="addon-sub">
                          {photoOpAddon.selected ? 'Selected' : 'Not added'}
                          {photoOpAddon.selected && photoOpAddon.price > 0 ? ` · ${formatCurrency(photoOpAddon.price)}` : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                  {customDesign.utilized && (
                    customDesign.hasDetails ? (
                      <div className={`custom-design-details${customDesign.logoImageUrl ? '' : ' custom-design-details-no-logo'}`} style={{ marginTop: 14 }}>
                        {customDesign.logoImageUrl && (
                          <a
                            href={customDesign.logoImageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="custom-design-logo"
                            title="Open logo"
                          >
                            <img src={customDesign.logoImageUrl} alt="Uploaded logo" />
                          </a>
                        )}
                        <div className="custom-design-fields">
                          {customDesign.companyName && (
                            <div className="custom-design-field">
                              <span className="custom-design-label">Company</span>
                              <span>{customDesign.companyName}</span>
                            </div>
                          )}
                          {customDesign.website && (
                            <div className="custom-design-field">
                              <span className="custom-design-label">Website</span>
                              <a href={websiteHref(customDesign.website)} target="_blank" rel="noopener noreferrer">
                                {customDesign.website} <FaExternalLinkAlt size={10} />
                              </a>
                            </div>
                          )}
                          {customDesign.price > 0 && (
                            <div className="custom-design-field">
                              <span className="custom-design-label">Design fee</span>
                              <span>{formatCurrency(customDesign.price)}</span>
                            </div>
                          )}
                        </div>
                        {customDesign.creativeCopy && (
                          <div className="custom-design-copy">{customDesign.creativeCopy}</div>
                        )}
                      </div>
                    ) : (
                      <p className="loading-text" style={{ margin: '12px 0 0' }}>
                        Custom design was selected, but no company, website, copy, or logo was submitted.
                      </p>
                    )
                  )}
                </div>

              {photoOpAddon.hasSessionFund && (
                <div className="detail-card">
                  <div className="card-header">
                    <h3>Photo Op Session Fund</h3>
                    <FaCreditCard className="card-icon" />
                  </div>
                  {photoOpFundPayment ? (
                    <div className="session-fund-fields">
                      <div className="session-fund-field">
                        <span className="custom-design-label">Status</span>
                        <span className={`badge ${photoOpFundConsumed ? 'badge-success' : 'badge-warning'}`}>
                          {photoOpFundConsumed ? 'Consumed' : 'Paid, not consumed'}
                        </span>
                      </div>
                      {(photoOpFundPayment.amountUsd != null || photoOpFundPayment.budgetUsd != null) && (
                        <div className="session-fund-field">
                          <span className="custom-design-label">Amount</span>
                          <span>
                            {formatCurrency(photoOpFundPayment.amountUsd ?? photoOpAddon.dueUsd)}
                            {photoOpFundPayment.budgetUsd != null ? ` · budget ${formatCurrency(photoOpFundPayment.budgetUsd)}` : ''}
                          </span>
                        </div>
                      )}
                      {photoOpFundPayment.transactionId && (
                        <div className="session-fund-field">
                          <span className="custom-design-label">Transaction</span>
                          <code>{photoOpFundPayment.transactionId}</code>
                        </div>
                      )}
                      {photoOpFundPayment.refId && (
                        <div className="session-fund-field">
                          <span className="custom-design-label">Ref ID</span>
                          <code>{photoOpFundPayment.refId}</code>
                        </div>
                      )}
                      {photoOpFundPaidAt && (
                        <div className="session-fund-field">
                          <span className="custom-design-label">Paid</span>
                          <span>{photoOpFundPaidAt}</span>
                        </div>
                      )}
                      {photoOpFundPayment.paymentType && (
                        <div className="session-fund-field">
                          <span className="custom-design-label">Type</span>
                          <span>{photoOpFundPayment.paymentType}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="loading-text" style={{ margin: 0 }}>
                      Session fund checkout is pending
                      {photoOpAddon.dueUsd > 0 ? ` · due ${formatCurrency(photoOpAddon.dueUsd)}` : ''}.
                    </p>
                  )}
                  {photoOpAddon.pending && (
                    <div className="session-fund-pending">
                      <div className="custom-design-label" style={{ marginBottom: 6 }}>Pending link</div>
                      {photoOpAddon.pending.name && <div><strong>{photoOpAddon.pending.name}</strong></div>}
                      {photoOpAddon.pending.signKey && <div>Sign: {photoOpAddon.pending.signKey}</div>}
                      {(photoOpAddon.pending.windowStart || photoOpAddon.pending.windowEnd) && (
                        <div>
                          Window: {photoOpAddon.pending.windowStart || '—'} → {photoOpAddon.pending.windowEnd || '—'}
                        </div>
                      )}
                      {photoOpAddon.pending.totalPlays != null && <div>Plays: {photoOpAddon.pending.totalPlays}</div>}
                      {photoOpAddon.pending.totalBudget != null && <div>Budget: {formatCurrency(photoOpAddon.pending.totalBudget)}</div>}
                      {photoOpPendingSavedAt && <div>Saved: {photoOpPendingSavedAt}</div>}
                    </div>
                  )}
                </div>
              )}

              {/* Campaign Dates */}
              <div className="detail-card">
                <div className="card-header">
                  <h3>Campaign Dates</h3>
                </div>
                <div className="date-inputs">
                  <div>
                    <label>Start Date</label>
                    <input type="date" className="input" value={editStartDate} onChange={e => setEditStartDate(e.target.value)} />
                  </div>
                  <div>
                    <label>End Date</label>
                    <input type="date" className="input" value={editEndDate} onChange={e => setEditEndDate(e.target.value)} />
                  </div>
                </div>
                <button className="btn btn-sm btn-secondary mt-2" onClick={handleSaveDates} disabled={loading}>
                  {loading ? <FaSpinner className="fa-spin" /> : 'Update Dates'}
                </button>
              </div>

              {/* Pacing Status (DSP Campaigns Only) */}
              {isDspCampaign() && pacingData && (
                <div className="detail-card">
                  <div className="card-header">
                    <h3>Pacing Status</h3>
                  </div>
                  <div className="pacing-overview">
                    <div className="pacing-metric">
                      <span className="label">Health Today</span>
                      <span className={`value ${getHealthClass(pacingData.plan?.health?.healthToday)}`}>
                        {pacingData.plan?.health?.healthToday?.toFixed(1) || 'N/A'}%
                      </span>
                    </div>
                    <div className="pacing-metric">
                      <span className="label">Daily Budget</span>
                      <span className="value">${pacingData.plan?.budgets?.dailyBudgetLimit?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="pacing-metric">
                      <span className="label">Spent Today</span>
                      <span className="value">${pacingData.plan?.budgets?.spentToday?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* DSP Controls */}
              {isDspCampaign() && (
                <>
                  <div className="detail-card card-highlight-blue">
                    <div className="card-header">
                      <h3>DSP Campaign</h3>
                      <FaPlay className="card-icon" />
                    </div>
                    <div className="dsp-status">
                      <div>
                        <strong>Status:</strong> {campaign.isActivated ? 'Active & Bidding' : 'Paused'}
                      </div>
                      <button
                        className={`btn btn-sm ${campaign.isActivated ? 'btn-danger' : 'btn-success'}`}
                        onClick={handleToggleCampaignActivation}
                        disabled={loading || (!campaign.isActivated && !!campaign.activationBlocked)}
                        title={!campaign.isActivated && campaign.activationBlocked ? (campaign.activationBlockedReason || 'Activation is blocked') : undefined}
                      >
                        {campaign.isActivated ? <><FaPause /> Deactivate</> : <><FaPlay /> Activate</>}
                      </button>
                    </div>
                    {campaign.activationBlocked && (
                      <div className="alert alert-warning" style={{ marginTop: 12 }}>
                        <FaExclamationTriangle /> {campaign.activationBlockedReason || 'Activation is blocked for this campaign.'}
                      </div>
                    )}
                    <div style={{ marginTop: 12 }}>
                      <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Activation block</label>
                      <textarea
                        className="input textarea"
                        placeholder="Reason shown in UI and API errors when activation is blocked..."
                        value={activationBlockReason}
                        onChange={e => setActivationBlockReason(e.target.value)}
                        rows={2}
                      />
                      <div className="action-buttons" style={{ marginTop: 8 }}>
                        {campaign.activationBlocked ? (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={() => handleSaveActivationBlock(true)}
                              disabled={loading || !activationBlockReason.trim()}
                            >
                              {loading ? <FaSpinner className="fa-spin" /> : 'Update block reason'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleSaveActivationBlock(false)}
                              disabled={loading}
                            >
                              Clear activation block
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleSaveActivationBlock(true)}
                            disabled={loading || !activationBlockReason.trim()}
                          >
                            {loading ? <FaSpinner className="fa-spin" /> : 'Block activation'}
                          </button>
                        )}
                      </div>
                    </div>
                    {!campaign.isWarmup && !campaign.iswarmup && !String(campaign.id || '').startsWith('warmup-') && !campaign.warmupDspCampaignId && !campaign.warmupTakeoverId && (
                      <div style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setWarmupSignKey(warmupSignOptions[0]?.key || '');
                            setShowWarmupModal(true);
                          }}
                          disabled={warmupLoading || loading || warmupSignOptions.length === 0}
                          title="Pick a sign, then create a mirrored warm-up campaign with that sign's creatives and deals."
                        >
                          {warmupLoading ? <><FaSpinner className="fa-spin" style={{ marginRight: 6 }} /> Creating…</> : 'Create Warm-Up Campaign'}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="detail-card">
                    <div className="card-header">
                      <h3>Budget & Pacing</h3>
                    </div>
                    <div className="budget-inputs">
                      <div>
                        <label>Total Budget</label>
                        <input type="number" step="0.01" className="input" value={editBudget} onChange={e => setEditBudget(parseFloat(e.target.value) || 0)} />
                      </div>
                      <div>
                        <label>Max CPM</label>
                        <input type="number" step="0.01" className="input" value={editMaxCPM} onChange={e => setEditMaxCPM(parseFloat(e.target.value) || 0)} />
                      </div>
                      <div>
                        <label>Pacing Mode</label>
                        <select className="input" value={editPacingMode} onChange={e => setEditPacingMode(e.target.value)}>
                          <option value="even">Even</option>
                          <option value="asap">ASAP</option>
                          <option value="front_loaded">Front Loaded</option>
                        </select>
                      </div>
                    </div>
                    <button className="btn btn-sm btn-primary mt-2" onClick={handleUpdateBudget} disabled={loading}>
                      {loading ? <FaSpinner className="fa-spin" /> : 'Update Budget'}
                    </button>
                  </div>
                </>
              )}

              {/* Moderation */}
              <div className="detail-card card-full-width card-highlight-yellow">
                <div className="card-header">
                  <h3>Admin Moderation</h3>
                </div>
                <textarea 
                  className="input textarea" 
                  placeholder="Rejection reason..." 
                  value={rejectReason} 
                  onChange={e => setRejectReason(e.target.value)}
                />
                <div className="action-buttons">
                  <button className="btn btn-success" onClick={() => handleUpdateStatus('Paused')} disabled={loading}>
                    {loading ? <FaSpinner className="fa-spin" /> : <><FaCheck /> Approve</>}
                  </button>
                  <button className="btn btn-danger" onClick={() => handleUpdateStatus('Rejected')} disabled={loading || !rejectReason.trim()}>
                    Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'performance' && isDspCampaign() && (
          <div className="tab-content">
            {analyticsLoading ? (
              <div className="loading-state">
                <FaSpinner className="fa-spin" size={32} />
                <p>Loading analytics...</p>
              </div>
            ) : analyticsData ? (
              <div className="detail-grid">
                {/* KPI Cards */}
                <div className="detail-card">
                  <div className="metric">
                    <span className="metric-label">Total Spend</span>
                    <span className="metric-value metric-value-large">{formatCurrency(analyticsData.totalSpend)}</span>
                  </div>
                </div>
                <div className="detail-card">
                  <div className="metric">
                    <span className="metric-label">Impressions</span>
                    <span className="metric-value metric-value-large text-success">{analyticsData.totalImpressions.toLocaleString()}</span>
                  </div>
                </div>
                <div className="detail-card">
                  <div className="metric">
                    <span className="metric-label">Playouts</span>
                    <span className="metric-value metric-value-large text-primary">{analyticsData.totalPlayouts.toLocaleString()}</span>
                  </div>
                </div>
                <div className="detail-card">
                  <div className="metric">
                    <span className="metric-label">Avg CPM</span>
                    <span className="metric-value metric-value-large text-purple">{formatCurrency(analyticsData.avgCPM)}</span>
                  </div>
                </div>

                {/* Impressions Trend Chart */}
                <div className="detail-card card-full-width">
                  <div className="card-header">
                    <h3><FaChartLine /> Impressions Trend</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <AreaChart data={impressionsTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="impressions" stroke={CHART_COLORS.primary} fill={CHART_COLORS.primary} fillOpacity={0.3} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Spend Trend Chart */}
                <div className="detail-card card-full-width">
                  <div className="card-header">
                    <h3><FaChartBar /> Daily Spend</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart data={spendTrendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="totalCost" fill={CHART_COLORS.success} name="Total Cost" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Top Signs Performance */}
                {signPerformanceData.length > 0 && (
                  <div className="detail-card card-full-width">
                    <div className="card-header">
                      <h3>Top Signs Performance</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <BarChart data={signPerformanceData} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="signId" type="category" width={150} />
                        <Tooltip formatter={(value) => value.toLocaleString()} />
                        <Legend />
                        <Bar dataKey="impressions" fill={CHART_COLORS.purple} name="Impressions" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Pacing Health */}
                {pacingLoading ? (
                  <div className="detail-card card-full-width">
                    <div className="loading-state">
                      <FaSpinner className="fa-spin" />
                      <p>Loading pacing data...</p>
                    </div>
                  </div>
                ) : pacingError ? (
                  <div className="detail-card card-full-width">
                    <div className="alert alert-danger">
                      <FaTimesCircle /> {pacingError}
                    </div>
                  </div>
                ) : pacingData ? (
                  <div className="detail-card card-full-width">
                    <div className="card-header">
                      <h3>Pacing Health</h3>
                    </div>
                    <div className="pacing-grid">
                      <div className="metric">
                        <span className="metric-label">Health Today</span>
                        <span className={`metric-value ${getHealthClass(pacingData.plan?.health?.healthToday)}`}>
                          {pacingData.plan?.health?.healthToday?.toFixed(1) || 'N/A'}%
                        </span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Spent Today</span>
                        <span className="metric-value">${pacingData.plan?.budgets?.spentToday?.toFixed(2) || '0.00'}</span>
                      </div>
                      <div className="metric">
                        <span className="metric-label">Daily Limit</span>
                        <span className="metric-value">${pacingData.plan?.budgets?.dailyBudgetLimit?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="empty-state">No analytics data available</div>
            )}
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="tab-content">
            <div className="schedule-container-modern">
              <div className="schedule-header-modern">
                <h3>Campaign Schedule</h3>
                <div className="schedule-actions-top">
                  <button className="btn btn-primary" onClick={handleSaveSchedule} disabled={scheduleSaving}>
                    {scheduleSaving ? <><FaSpinner className="fa-spin" /> Saving...</> : <><FaCheck /> Save Schedule</>}
                  </button>
                </div>
              </div>

              <div className="schedule-templates-modern">
                <span className="templates-label">Quick Templates:</span>
                <button className="template-btn" onClick={() => setScheduleTemplate('weekdays-9-5')}>
                  <FaClock /> Weekdays 9-5
                </button>
                <button className="template-btn" onClick={() => setScheduleTemplate('weekends')}>
                  <FaClock /> Weekends
                </button>
                <button className="template-btn" onClick={() => setScheduleTemplate('all')}>
                  <FaClock /> 24/7
                </button>
                <button className="template-btn template-btn-clear" onClick={() => setScheduleTemplate('clear')}>
                  <FaTrash /> Clear All
                </button>
              </div>

              <div className="schedule-grid-modern">
                {timeSegmentsByDay.map((daySegs, dayIndex) => {
                  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                  const hasSegments = daySegs.length > 0;
                  
                  return (
                    <div key={dayIndex} className={`schedule-day-card ${hasSegments ? 'has-segments' : 'no-segments'}`}>
                      <div className="day-card-header">
                        <div className="day-name-row">
                          <span className="day-name">{dayNames[dayIndex]}</span>
                          <span className="day-abbr">{daysOfWeekShort[dayIndex]}</span>
                        </div>
                        <button className="add-segment-btn" onClick={() => addTimeSegment(dayIndex)}>
                          <FaPlus /> Add
                        </button>
                        {hasSegments && (
                          <button
                            type="button"
                            className="add-segment-btn"
                            onClick={() => copyDaySchedule(dayIndex)}
                            title="Copy this day's hours onto every other day"
                          >
                            <FaCopy /> Copy to all
                          </button>
                        )}
                      </div>
                      
                      <div className="day-card-body">
                        {daySegs.length === 0 ? (
                          <div className="empty-day-message">
                            <FaTimesCircle /> No schedule
                          </div>
                        ) : (
                          <div className="segments-list">
                            {daySegs.map((seg, segIdx) => (
                              <div key={seg.id} className="time-segment-row">
                                <span className="segment-number">{segIdx + 1}</span>
                                <div className="time-inputs-group">
                                  <input 
                                    type="time" 
                                    className="time-input" 
                                    value={seg.startTime} 
                                    onChange={e => updateTimeSegment(dayIndex, seg.id, 'startTime', e.target.value)} 
                                  />
                                  <span className="time-separator">→</span>
                                  <input 
                                    type="time" 
                                    className="time-input" 
                                    value={seg.endTime} 
                                    onChange={e => updateTimeSegment(dayIndex, seg.id, 'endTime', e.target.value)} 
                                  />
                                </div>
                                <button className="delete-segment-btn" onClick={() => deleteTimeSegment(dayIndex, seg.id)}>
                                  <FaTrash />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'creatives' && (
          <div className="tab-content">
            {customDesign.utilized && (
              <div className="detail-card card-full-width card-highlight-blue" style={{ marginBottom: 16 }}>
                <div className="card-header">
                  <h3>Custom Design Package</h3>
                  <span className="badge badge-primary"><FaCheckCircle /> Requested</span>
                </div>
                {customDesign.hasDetails ? (
                  <div className={`custom-design-details${customDesign.logoImageUrl ? '' : ' custom-design-details-no-logo'}`}>
                    {customDesign.logoImageUrl && (
                      <a
                        href={customDesign.logoImageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="custom-design-logo"
                        title="Open logo"
                      >
                        <img src={customDesign.logoImageUrl} alt="Uploaded logo" />
                      </a>
                    )}
                    <div className="custom-design-fields">
                      {customDesign.companyName && (
                        <div className="custom-design-field">
                          <span className="custom-design-label">Company</span>
                          <span>{customDesign.companyName}</span>
                        </div>
                      )}
                      {customDesign.website && (
                        <div className="custom-design-field">
                          <span className="custom-design-label">Website</span>
                          <a href={websiteHref(customDesign.website)} target="_blank" rel="noopener noreferrer">
                            {customDesign.website} <FaExternalLinkAlt size={10} />
                          </a>
                        </div>
                      )}
                      {customDesign.price > 0 && (
                        <div className="custom-design-field">
                          <span className="custom-design-label">Design fee</span>
                          <span>{formatCurrency(customDesign.price)}</span>
                        </div>
                      )}
                    </div>
                    {customDesign.creativeCopy && (
                      <div className="custom-design-copy">{customDesign.creativeCopy}</div>
                    )}
                  </div>
                ) : (
                  <p className="loading-text" style={{ margin: 0 }}>
                    Custom design was selected. No logo, company, website, or copy was submitted with the request.
                  </p>
                )}
              </div>
            )}
            {Object.keys(creativesByFormat).length > 0 ? (
              <div className="creative-library">
                {Object.entries(creativesByFormat).map(([key, list]) => {
                  if (!Array.isArray(list) || !list.length) return null;
                  return (
                    <div key={key} className="creative-format-section">
                      <div className="creative-format-header">
                        <h3>{key}</h3>
                        <span className="badge badge-secondary">{list.length} creative{list.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="creative-grid-large">
                        {list.map((c, i) => {
                          const thumbUrl = c.croppedImageUrl || c.originalImageUrl || c.url;
                          const statusPres = getCreativeStatusPresentation(c);
                          return (
                            <div key={c.id || i} className="creative-card-large">
                              <div className="creative-media-large">
                                {thumbUrl && isVideo(thumbUrl) ? (
                                  <video src={thumbUrl} className="creative-preview-large" controls muted />
                                ) : thumbUrl ? (
                                  <img src={thumbUrl} className="creative-preview-large" alt={c.title || 'Creative'} onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <div className="creative-preview-placeholder">No preview</div>
                                )}
                              </div>
                              <div className="creative-card-info">
                                <div className="creative-card-title">{c.title || c.fileName || 'Untitled'}</div>
                                <div className="creative-card-meta">
                                  <span className={`status-badge status-${statusPres.slug}`}>{statusPres.label}</span>
                                  {(c.width && c.height) ? (
                                    <span className="creative-dimensions">{c.width}x{c.height}</span>
                                  ) : null}
                                </div>
                                {(c.advertiser_domain || c.clientDomain || c.domain) && (
                                  <div className="creative-card-domain">
                                    {c.advertiser_domain || c.clientDomain || c.domain}
                                  </div>
                                )}
                                {c.dspCreativeId && (
                                  <div className="creative-card-dspid">{c.dspCreativeId}</div>
                                )}
                                <div className="creative-card-actions">
                                  <button className="btn btn-sm btn-secondary" onClick={() => openCreativeDetailModal({ ...c, formatKey: key })}>
                                    <FaPen /> Edit
                                  </button>
                                  {isCreativeSubmittedToPerion(c) && (
                                    <button
                                      className="btn btn-sm btn-secondary"
                                      disabled={loading}
                                      onClick={() => handleResubmitCreative({ ...c, formatKey: key })}
                                    >
                                      <FaRedo /> Resubmit
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <FaExclamationTriangle />
                <p>No creatives on this campaign yet.</p>
              </div>
            )}

            {/* Creative Approval Queue */}
            {(flaggedCreatives.length > 0 ||
              campaign.status === 'Pending Vue Approval' ||
              campaign.status === 'Awaiting Approval' ||
              campaign.status === 'Draft') && (
              <div className="detail-card card-full-width card-highlight-red mt-4">
                <div className="card-header">
                  <h3>Creatives Pending Approval ({flaggedCreatives.length})</h3>
                </div>
                {creativesLoading ? (
                  <div className="loading-state">
                    <FaSpinner className="fa-spin" />
                    <p>Loading creatives...</p>
                  </div>
                ) : flaggedCreatives.length > 0 ? (
                  <div className="creatives-approval-list">
                    {flaggedCreatives.map(creative => (
                      <div key={creative.id} className="creative-approval-item">
                        {isVideo(creative.croppedImageUrl || creative.originalImageUrl) ? (
                          <video src={creative.croppedImageUrl || creative.originalImageUrl} className="creative-approval-thumb" muted />
                        ) : (
                          <img src={creative.croppedImageUrl || creative.originalImageUrl} className="creative-approval-thumb" alt="" />
                        )}
                        <div className="creative-approval-info">
                          <div className="creative-approval-title">{creative.title || creative.fileName}</div>
                          <div className="creative-approval-res">Resolution: {creative.formatKey || `${creative.width}x${creative.height}`}</div>
                          <div className="mt-2">
                            <label className="label-sm">IAB Category (Required)</label>
                            {iabCategoriesLoading ? (
                              <div className="text-sm">Loading categories...</div>
                            ) : (
                              <button
                                type="button"
                                className="input input-sm iab-category-select"
                                style={{ textAlign: 'left', cursor: 'pointer' }}
                                onClick={() => openIabPicker(creative)}
                                disabled={loading}
                              >
                                {getEffectiveIabCodeForCreative(creative) ? (
                                  <>
                                    <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                      {getEffectiveIabCodeForCreative(creative)}
                                    </span>
                                    <span style={{ color: '#64748b' }}>
                                      {' '}
                                      — {getIabName(getEffectiveIabCodeForCreative(creative))}
                                    </span>
                                  </>
                                ) : (
                                  <span style={{ color: '#64748b' }}>Search & select category…</span>
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="creative-approval-actions">
                          <button 
                            className="btn btn-sm btn-success" 
                            onClick={() => handleApproveCreative(creative)} 
                            disabled={loading || !getEffectiveIabCodeForCreative(creative)}
                          >
                            <FaCheck /> Approve
                          </button>
                          <button 
                            className="btn btn-sm btn-danger" 
                            onClick={() => handleRejectCreative(creative)} 
                            disabled={loading}
                          >
                            <FaTimes /> Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">No creatives pending approval</div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'rtb' && (
          <div className="tab-content">
            <div className="rtb-events-container">
              <div className="rtb-header">
                <h3><FaBolt /> Real-Time Bidding Events</h3>
                <div className="rtb-controls">
                  <div className="rtb-search">
                    <FaSearch />
                    <input 
                      type="text" 
                      placeholder="Search by auction, sign, reason..." 
                      value={rtbSearch}
                      onChange={e => setRtbSearch(e.target.value)}
                      className="input input-sm"
                    />
                  </div>
                  
                  <select value={rtbDateRange} onChange={e => setRtbDateRange(e.target.value)} className="input input-sm">
                    <option value="24h">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="custom">Custom Range</option>
                  </select>
                  
                  {rtbDateRange === 'custom' && (
                    <>
                      <input 
                        type="date" 
                        value={rtbStartDate}
                        onChange={e => setRtbStartDate(e.target.value)}
                        className="input input-sm"
                        style={{ width: '140px' }}
                      />
                      <input 
                        type="date" 
                        value={rtbEndDate}
                        onChange={e => setRtbEndDate(e.target.value)}
                        className="input input-sm"
                        style={{ width: '140px' }}
                      />
                    </>
                  )}
                  
                  <select value={rtbFilter} onChange={e => setRtbFilter(e.target.value)} className="input input-sm">
                    <option value="all">All Events</option>
                    <option value="win">Wins</option>
                    <option value="loss">Losses</option>
                    <option value="impression">Impressions</option>
                    <option value="bid">Bids</option>
                    <option value="no_bid">No Bids</option>
                  </select>
                  
                  <select value={rtbGroupBy} onChange={e => setRtbGroupBy(e.target.value)} className="input input-sm">
                    <option value="hour">By Hour</option>
                    <option value="day">By Day</option>
                  </select>
                  
                  <button className="btn btn-sm btn-primary" onClick={fetchHistoricalRtbEvents} disabled={rtbLoading}>
                    {rtbLoading ? <FaSpinner className="fa-spin" /> : <><FaFilter /> Apply</>}
                  </button>
                </div>
              </div>

              {rtbSummary && (
                <div className="metrics-grid" style={{ marginBottom: 16 }}>
                  <div className="metric">
                    <span className="metric-label">Archive events</span>
                    <span className="metric-value">{Number(rtbSummary.total || rtbArchiveTotal || 0).toLocaleString()}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Wins</span>
                    <span className="metric-value text-success">{Number(rtbSummary.wins || 0).toLocaleString()}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Losses</span>
                    <span className="metric-value text-danger">{Number(rtbSummary.losses || 0).toLocaleString()}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">No bids</span>
                    <span className="metric-value">{Number(rtbSummary.noBids || 0).toLocaleString()}</span>
                  </div>
                  <div className="metric">
                    <span className="metric-label">Win rate</span>
                    <span className="metric-value">{Number(rtbSummary.winRate || 0)}%</span>
                  </div>
                </div>
              )}
              
              {/* Charts Section */}
              {(() => {
                // Combine all events for analytics
                const allEvents = [...liveRtbEvents, ...rtbEvents];
                
                // Live RTB Events Summary for Pie Chart
                const liveCounts = {
                  win: liveRtbEvents.filter(e => e.type === 'win').length,
                  impression: liveRtbEvents.filter(e => e.type === 'impression').length,
                  loss: liveRtbEvents.filter(e => e.type === 'loss').length,
                  no_bid: liveRtbEvents.filter(e => e.type === 'no_bid').length,
                  bid: liveRtbEvents.filter(e => e.type === 'bid').length
                };
                const liveTotal = Object.values(liveCounts).reduce((a, b) => a + b, 0);
                const livePieData = [
                  { name: 'Wins', value: liveCounts.win, color: '#22c55e' },
                  { name: 'Impressions', value: liveCounts.impression, color: '#10b981' },
                  { name: 'Losses', value: liveCounts.loss, color: '#ef4444' },
                  { name: 'No Bids', value: liveCounts.no_bid, color: '#f59e0b' },
                  { name: 'Bids', value: liveCounts.bid, color: '#6b7280' }
                ].filter(d => d.value > 0);
                
                // Group by time period for time-series chart
                const timeData = {};
                const dateFormat = rtbGroupBy === 'day' ? 'MMM D' : 'MMM D, HH:00';
                allEvents.forEach(event => {
                  if (!event.timestamp) return;
                  const timeKey = dayjs(event.timestamp).format(dateFormat);
                  if (!timeData[timeKey]) {
                    timeData[timeKey] = { time: timeKey, win: 0, loss: 0, impression: 0, no_bid: 0, bid: 0 };
                  }
                  const type = event.type || 'unknown';
                  if (timeData[timeKey][type] !== undefined) {
                    timeData[timeKey][type]++;
                  }
                });
                
                const timeChartData = Object.values(timeData).sort((a, b) => {
                  return dayjs(a.time, dateFormat).unix() - dayjs(b.time, dateFormat).unix();
                });
                
                // Aggregate loss reasons
                const lossReasonCounts = {};
                allEvents.filter(e => e.type === 'loss').forEach(event => {
                  const reason = event.reasonDescription || event.reason || 'Unknown';
                  lossReasonCounts[reason] = (lossReasonCounts[reason] || 0) + 1;
                });
                
                const lossReasonData = Object.entries(lossReasonCounts)
                  .map(([name, value]) => ({ name, value }))
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 10);
                
                const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9'];
                
                return (
                  <div className="rtb-charts-section">
                    {/* Live Events Summary - Single Row */}
                    {rtbChartData.liveTotal > 0 && (
                      <div className="detail-card card-full-width">
                        <div className="card-header">
                          <h3><FaBolt className="pulse" /> Live Bidding Activity (Last 30 min)</h3>
                          <span className="badge badge-success">{rtbChartData.liveTotal.toLocaleString()} events</span>
                        </div>
                        <div style={{ padding: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                          {rtbChartData.livePieData.map((item, idx) => (
                            <div key={idx} style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '12px',
                              padding: '12px 24px',
                              background: '#f9fafb',
                              borderRadius: '8px',
                              border: `2px solid ${item.color}30`,
                              minWidth: '140px'
                            }}>
                              <div style={{ width: 12, height: 12, borderRadius: '50%', background: item.color }} />
                              <div>
                                <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{item.name}</div>
                                <div style={{ fontSize: 20, fontWeight: 700, color: item.color }}>{item.value.toLocaleString()}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Time-series Outcomes Chart */}
                    {rtbChartData.timeChartData.length > 0 && (
                      <div className="detail-card card-full-width mt-4">
                        <div className="card-header">
                          <h3>RTB Outcomes Over Time</h3>
                          <span className="badge badge-secondary">{(liveRtbEvents.length + rtbEvents.length).toLocaleString()} total events</span>
                        </div>
                        <div className="cd-chart-wrap" style={{ width: '100%', height: chartHeight, padding: isMobile ? 8 : 20 }}>
                          <ResponsiveContainer>
                            <AreaChart data={rtbChartData.timeChartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis dataKey="time" tick={{ fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                              <YAxis tick={{ fontSize: 11 }} />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: '#fff', 
                                  border: '1px solid #ccc', 
                                  borderRadius: '4px',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}
                              />
                              <Legend wrapperStyle={{ fontSize: 12 }} />
                              <Area type="monotone" dataKey="win" stackId="1" stroke="#22c55e" fill="#22c55e" name="Wins" />
                              <Area type="monotone" dataKey="impression" stackId="1" stroke="#10b981" fill="#10b981" name="Impressions" />
                              <Area type="monotone" dataKey="loss" stackId="1" stroke="#ef4444" fill="#ef4444" name="Losses" />
                              <Area type="monotone" dataKey="no_bid" stackId="1" stroke="#f59e0b" fill="#f59e0b" name="No Bids" />
                              <Area type="monotone" dataKey="bid" stackId="1" stroke="#6b7280" fill="#6b7280" name="Bids" />
                            </AreaChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                    
                    {/* Loss Reasons Chart */}
                    {rtbChartData.lossReasonData.length > 0 && (
                      <div className="detail-card card-full-width mt-4">
                        <div className="card-header">
                          <h3>Top Loss Reasons</h3>
                          <span className="badge badge-danger">{(liveRtbEvents.filter(e => e.type === 'loss').length + rtbEvents.filter(e => e.type === 'loss').length).toLocaleString()} total losses</span>
                        </div>
                        <div className="cd-chart-wrap cd-loss-chart" style={{ width: '100%', minHeight: isMobile ? 280 : 400, padding: isMobile ? 8 : 20, display: 'flex', gap: isMobile ? 16 : 40 }}>
                          <div style={{ flex: '0 0 400px' }}>
                            <ResponsiveContainer>
                              <PieChart>
                                <Pie
                                  data={rtbChartData.lossReasonData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                                  outerRadius={120}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {rtbChartData.lossReasonData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                            {rtbChartData.lossReasonData.map((reason, idx) => {
                              const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9'];
                              return (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px' }}>
                                  <div style={{ width: '16px', height: '16px', borderRadius: '2px', backgroundColor: COLORS[idx % COLORS.length], flexShrink: 0 }}></div>
                                  <div style={{ flex: 1, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reason.name}</div>
                                  <div style={{ fontWeight: 600, color: '#ef4444' }}>{reason.value.toLocaleString()}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Historical Events Section with Pagination */}
              {rtbLoading ? (
                <div className="loading-state">
                  <FaSpinner className="fa-spin" />
                  <p>Loading RTB events...</p>
                </div>
              ) : (() => {
                const filteredEvents = rtbEvents
                  .filter(event => {
                    if (rtbFilter === 'all') return true;
                    return event.type === rtbFilter;
                  })
                  .filter(event => {
                    if (!rtbSearch) return true;
                    const searchLower = rtbSearch.toLowerCase();
                    return (
                      event.signId?.toLowerCase().includes(searchLower) ||
                      event.auctionId?.toLowerCase().includes(searchLower) ||
                      event.reason?.toString().toLowerCase().includes(searchLower) ||
                      event.reasonDescription?.toLowerCase().includes(searchLower) ||
                      event.publisher?.toLowerCase().includes(searchLower)
                    );
                  });
                
                const totalPages = Math.ceil(filteredEvents.length / rtbEventsPerPage);
                const startIdx = (rtbPage - 1) * rtbEventsPerPage;
                const endIdx = startIdx + rtbEventsPerPage;
                const paginatedEvents = filteredEvents.slice(startIdx, endIdx);
                
                return filteredEvents.length > 0 ? (
                  <div className="rtb-section">
                    <div className="rtb-section-header">
                      <h4>Event Details</h4>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="badge badge-secondary">
                          {filteredEvents.length.toLocaleString()} loaded
                          {rtbArchiveTotal > filteredEvents.length ? ` of ${rtbArchiveTotal.toLocaleString()}` : ''}
                        </span>
                        {totalPages > 1 && (
                          <div className="pagination-controls">
                            <button 
                              className="btn btn-sm btn-secondary" 
                              onClick={() => setRtbPage(Math.max(1, rtbPage - 1))}
                              disabled={rtbPage === 1}
                            >
                              Previous
                            </button>
                            <span style={{ fontSize: '13px', color: '#6b7280' }}>
                              Page {rtbPage} of {totalPages}
                            </span>
                            <button 
                              className="btn btn-sm btn-secondary" 
                              onClick={() => setRtbPage(Math.min(totalPages, rtbPage + 1))}
                              disabled={rtbPage === totalPages}
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="rtb-events-list">
                      {paginatedEvents.map((event, idx) => (
                        <div key={event.id || idx} className={`rtb-event-card ${event.type || 'bid'}`}>
                          <div className="rtb-event-header">
                            <div className="rtb-event-status">
                              {event.type === 'win' && <span className="badge badge-success"><FaCheck /> WIN</span>}
                              {event.type === 'impression' && <span className="badge badge-success"><FaCheckCircle /> IMPRESSION</span>}
                              {event.type === 'loss' && <span className="badge badge-danger"><FaTimes /> LOSS</span>}
                              {event.type === 'no_bid' && <span className="badge badge-warning"><FaTimesCircle /> NO BID</span>}
                              {event.type === 'bid' && <span className="badge badge-secondary"><FaBolt /> BID</span>}
                              {!event.type && <span className="badge badge-secondary"><FaBolt /> UNKNOWN</span>}
                            </div>
                            <div className="rtb-event-time">
                              {event.timestamp ? dayjs(event.timestamp).format('MMM D, HH:mm:ss') : 'N/A'}
                            </div>
                          </div>
                          <div className="rtb-event-body">
                            <div className="rtb-event-row">
                              <span className="label">Auction ID:</span>
                              <code>{event.auctionId || 'N/A'}</code>
                            </div>
                            {event.signId && (
                              <div className="rtb-event-row">
                                <span className="label">Sign ID:</span>
                                <code>{event.signId}</code>
                              </div>
                            )}
                            {event.publisher && (
                              <div className="rtb-event-row">
                                <span className="label">Publisher:</span>
                                <span>{event.publisher}</span>
                              </div>
                            )}
                            {(event.bidPrice > 0 || event.bidFloor > 0) && (
                              <div className="rtb-event-row">
                                <span className="label">Bid:</span>
                                <span>
                                  {event.bidPrice > 0 ? `$${Number(event.bidPrice).toFixed(2)}` : '—'}
                                  {event.bidFloor > 0 ? ` / floor $${Number(event.bidFloor).toFixed(2)}` : ''}
                                </span>
                              </div>
                            )}
                            {(event.reason || event.reasonDescription) && (
                              <div className="rtb-event-row">
                                <span className="label">Reason:</span>
                                <span>{event.reasonDescription || event.reason}</span>
                              </div>
                            )}
                            {event.detail && event.detail !== 'No details provided' && (
                              <div className="rtb-event-row">
                                <span className="label">Detail:</span>
                                <span>{event.detail}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="rtb-pagination-footer">
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => setRtbPage(Math.max(1, rtbPage - 1))}
                          disabled={rtbPage === 1}
                        >
                          Previous
                        </button>
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>
                          Showing {startIdx + 1}-{Math.min(endIdx, filteredEvents.length)} of {filteredEvents.length.toLocaleString()}
                        </span>
                        <button 
                          className="btn btn-sm btn-secondary" 
                          onClick={() => setRtbPage(Math.min(totalPages, rtbPage + 1))}
                          disabled={rtbPage === totalPages}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="empty-state">
                    <FaBolt size={48} />
                    <p>No RTB events found. Try adjusting your filters.</p>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {activeTab === 'signs' && (
          <div className="tab-content">
            <div className="signs-grid-modern">
              {campaign.signs?.map((sign, idx) => {
                const signId = getSignKey(sign);
                const isTargeted = sign.isTargeted !== false;
                const hasStats = analyticsData?.bySign?.find(s => s.signId === signId || s.signId === sign.openrtb_uuid);
                
                return (
                  <div key={idx} className={`sign-card-modern ${!isTargeted ? 'sign-disabled' : ''}`}>
                    <div className="sign-header-modern">
                      {sign.asset_image_url ? (
                        <img src={sign.asset_image_url} className="sign-thumbnail" alt="" />
                      ) : (
                        <div className="sign-thumbnail-placeholder">
                          <FaMapMarkerAlt size={32} />
                        </div>
                      )}
                      <div className="sign-status-badge">
                        {isTargeted ? (
                          <span className="badge badge-success"><FaCheckCircle /> Active</span>
                        ) : (
                          <span className="badge badge-secondary"><FaTimesCircle /> Paused</span>
                        )}
                      </div>
                    </div>

                    <div className="sign-body-modern">
                      <h4 className="sign-title">{getSignLabel(sign)}</h4>
                      <p className="sign-location">
                        <FaMapMarkerAlt /> {sign.address || [sign.city, sign.state].filter(Boolean).join(', ') || 'Location unavailable'}
                      </p>
                      <p className="sign-id-line">{signId || 'No OpenRTB UUID'}</p>

                      <div className="sign-metrics-row">
                        <div className="metric-item">
                          <span className="metric-icon"><FaPlay /></span>
                          <div>
                            <div className="metric-value">{sign.dailyPlays?.toLocaleString() || 0}</div>
                            <div className="metric-label">Plays/Day</div>
                          </div>
                        </div>
                        <div className="metric-item">
                          <span className="metric-icon"><FaCreditCard /></span>
                          <div>
                            <div className="metric-value">{formatCurrency(sign.mediaCost)}</div>
                            <div className="metric-label">Media Cost</div>
                          </div>
                        </div>
                      </div>

                      {isDspCampaign() && hasStats && (
                        <div className="sign-performance-modern">
                          <div className="perf-stat">
                            <FaEye /> <strong>{hasStats.impressions.toLocaleString()}</strong> impr
                          </div>
                          <div className="perf-stat">
                            <FaPlay /> <strong>{hasStats.playouts}</strong> plays
                          </div>
                          <div className="perf-stat">
                            <FaCreditCard /> <strong>{formatCurrency(hasStats.totalCost)}</strong>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="sign-footer-modern">
                      {isDspCampaign() && (
                        <button 
                          className={`btn btn-sm ${isTargeted ? 'btn-danger' : 'btn-success'}`} 
                          onClick={() => handleToggleSignTargeting(signId, !isTargeted)} 
                          disabled={loading}
                        >
                          {isTargeted ? <><FaPause /> Pause</> : <><FaPlay /> Activate</>}
                        </button>
                      )}
                      {isDspCampaign() && (liveRtbEvents.length > 0 || rtbEvents.length > 0) && (
                        <button 
                          className="btn btn-sm btn-info" 
                          onClick={() => openSignStatsModal(sign)}
                          title="View RTB Statistics"
                        >
                          <FaBolt /> RTB Stats
                        </button>
                      )}
                      <button className="btn btn-sm btn-secondary" onClick={() => openSignCreativesModal(sign)}>
                        <FaPen /> Manage Creatives
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Per-sign creatives modal */}
      <ReactModal
        isOpen={showSignCreativesModal}
        onRequestClose={closeSignCreativesModal}
        ariaHideApp={false}
        className="sub-modal"
        overlayClassName="sub-modal-overlay"
        style={SUB_MODAL_STYLE}
      >
        <div className="modal-header">
          <h2>Assign Creatives to Sign</h2>
          <button className="modal-close" onClick={closeSignCreativesModal}><FaClose /></button>
        </div>
        <div className="modal-body">
          {signCreativesModalSign && (
            <>
              <div className="modal-sign-info">
                <strong>{signCreativesModalSign.asset_name}</strong>
                <span>{signCreativesModalSign.address}</span>
              </div>
              <div className="creative-select-list">
                {getAvailableCreativesForSign(signCreativesModalSign).map((c, i) => {
                  const key = getCreativeUniqKey(c);
                  const isSelected = signCreativesSelectedKeys.includes(key);
                  return (
                    <div 
                      key={i} 
                      className={`creative-select-item ${isSelected ? 'selected' : ''}`} 
                      onClick={() => {
                        if (isSelected) {
                          setSignCreativesSelectedKeys(signCreativesSelectedKeys.filter(k => k !== key));
                        } else {
                          setSignCreativesSelectedKeys([...signCreativesSelectedKeys, key]);
                        }
                      }}
                    >
                      {isVideo(c.croppedImageUrl || c.originalImageUrl) ? (
                        <video src={c.croppedImageUrl || c.originalImageUrl} className="creative-select-thumb" muted />
                      ) : (
                        <img src={c.croppedImageUrl || c.originalImageUrl} className="creative-select-thumb" alt="" />
                      )}
                      <div className="creative-select-check">{isSelected && <FaCheck />}</div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={resetSignCreativesToDefault}>Reset to Default</button>
          <div className="modal-footer-actions">
            <button className="btn btn-secondary" onClick={closeSignCreativesModal}>Cancel</button>
            <button className="btn btn-primary" onClick={saveSignCreativesSelection}>Save</button>
          </div>
        </div>
      </ReactModal>

      {/* Sign RTB Statistics Modal */}
      <ReactModal
        isOpen={showSignStatsModal}
        onRequestClose={closeSignStatsModal}
        ariaHideApp={false}
        className="sub-modal"
        overlayClassName="sub-modal-overlay"
        style={SUB_MODAL_STYLE}
      >
        <div className="modal-header">
          <h2><FaBolt /> RTB Statistics for Sign</h2>
          <button className="modal-close" onClick={closeSignStatsModal}><FaClose /></button>
        </div>
        <div className="modal-body">
          {signStatsModalSign && (() => {
            const stats = getSignRtbStats(signStatsModalSign);
            const signId = signStatsModalSign.openRTBUUID || signStatsModalSign.openrtb_uuid || signStatsModalSign.adunit_id || signStatsModalSign.signId || signStatsModalSign.id || '';
            
            return (
              <>
                <div className="modal-sign-info">
                  <strong>{signStatsModalSign.asset_name}</strong>
                  <span>{signStatsModalSign.address || `${signStatsModalSign.city}, ${signStatsModalSign.state}`}</span>
                  <code style={{ fontSize: '11px', display: 'block', marginTop: '8px', opacity: 0.7 }}>
                    Sign ID: {signId}
                  </code>
                </div>

                {stats && stats.total > 0 ? (
                  <>
                    {/* Summary Statistics */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginTop: '20px' }}>
                      <div className="stat-card" style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                        <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Total Events</div>
                        <div style={{ fontSize: '24px', fontWeight: 600, color: '#1e293b' }}>{stats.total.toLocaleString()}</div>
                      </div>
                      <div className="stat-card" style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                        <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>Wins</div>
                        <div style={{ fontSize: '24px', fontWeight: 600, color: '#15803d' }}>{stats.wins.toLocaleString()}</div>
                      </div>
                      <div className="stat-card" style={{ background: '#fef2f2', padding: '16px', borderRadius: '8px', border: '1px solid #fecaca' }}>
                        <div style={{ fontSize: '12px', color: '#991b1b', marginBottom: '4px' }}>Losses</div>
                        <div style={{ fontSize: '24px', fontWeight: 600, color: '#dc2626' }}>{stats.losses.toLocaleString()}</div>
                      </div>
                      <div className="stat-card" style={{ background: '#f0fdf4', padding: '16px', borderRadius: '8px', border: '1px solid #86efac' }}>
                        <div style={{ fontSize: '12px', color: '#166534', marginBottom: '4px' }}>Impressions</div>
                        <div style={{ fontSize: '24px', fontWeight: 600, color: '#10b981' }}>{stats.impressions.toLocaleString()}</div>
                      </div>
                      <div className="stat-card" style={{ background: '#fffbeb', padding: '16px', borderRadius: '8px', border: '1px solid #fde68a' }}>
                        <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '4px' }}>No Bids</div>
                        <div style={{ fontSize: '24px', fontWeight: 600, color: '#f59e0b' }}>{stats.noBids.toLocaleString()}</div>
                      </div>
                      <div className="stat-card" style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #cbd5e1' }}>
                        <div style={{ fontSize: '12px', color: '#475569', marginBottom: '4px' }}>Bids</div>
                        <div style={{ fontSize: '24px', fontWeight: 600, color: '#64748b' }}>{stats.bids.toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Loss Reasons Breakdown */}
                    {stats.lossReasons.length > 0 && (
                      <div style={{ marginTop: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <FaExclamationTriangle style={{ color: '#dc2626' }} />
                          Loss Notifications Breakdown
                          <span className="badge badge-danger" style={{ marginLeft: 'auto' }}>
                            {stats.losses.toLocaleString()} total
                          </span>
                        </h3>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {stats.lossReasons.map((reason, idx) => {
                            const percentage = ((reason.value / stats.losses) * 100).toFixed(1);
                            return (
                              <div key={idx} style={{ 
                                background: '#fff', 
                                padding: '12px 16px', 
                                borderRadius: '6px', 
                                border: '1px solid #e5e7eb',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                              }}>
                                <div style={{ 
                                  minWidth: '32px', 
                                  height: '32px', 
                                  borderRadius: '50%', 
                                  background: '#fef2f2', 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  justifyContent: 'center',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: '#dc2626'
                                }}>
                                  {idx + 1}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: '14px', fontWeight: 500, color: '#1e293b', marginBottom: '2px' }}>
                                    {reason.name}
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                                    {percentage}% of losses
                                  </div>
                                </div>
                                <div style={{ 
                                  fontSize: '18px', 
                                  fontWeight: 600, 
                                  color: '#dc2626',
                                  minWidth: '60px',
                                  textAlign: 'right'
                                }}>
                                  {reason.value.toLocaleString()}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Win Rate Indicator */}
                        {stats.wins > 0 && (
                          <div style={{ marginTop: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>Win Rate</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ flex: 1, height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                                <div style={{ 
                                  height: '100%', 
                                  background: 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)',
                                  width: `${((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)}%`,
                                  transition: 'width 0.3s ease'
                                }}></div>
                              </div>
                              <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981', minWidth: '60px', textAlign: 'right' }}>
                                {((stats.wins / (stats.wins + stats.losses)) * 100).toFixed(1)}%
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    <FaBolt size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
                    <p>No RTB events found for this sign.</p>
                  </div>
                )}
              </>
            );
          })()}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={closeSignStatsModal}>Close</button>
        </div>
      </ReactModal>

      {/* Creative detail modal */}
      <ReactModal
        isOpen={!!selectedCreativeForDetail}
        onRequestClose={closeCreativeDetailModal}
        ariaHideApp={false}
        className="sub-modal"
        overlayClassName="sub-modal-overlay"
        style={SUB_MODAL_STYLE}
      >
        <div className="modal-header">
          <h2>Creative Details</h2>
          <button className="modal-close" onClick={closeCreativeDetailModal}><FaClose /></button>
        </div>
        <div className="modal-body">
          {selectedCreativeForDetail && (
            <>
              {isVideo(selectedCreativeForDetail.croppedImageUrl || selectedCreativeForDetail.originalImageUrl) ? (
                <video src={selectedCreativeForDetail.croppedImageUrl || selectedCreativeForDetail.originalImageUrl} controls className="creative-detail-preview" />
              ) : (
                <img src={selectedCreativeForDetail.croppedImageUrl || selectedCreativeForDetail.originalImageUrl} className="creative-detail-preview" alt="" />
              )}

              <p className="text-sm" style={{ marginTop: 12, color: '#64748b' }}>
                Advertiser domain: <strong>
                  {(() => {
                    const domainResult = getAdvertiserDomainDefaultsForApprove(
                      selectedCreativeForDetail,
                      campaign,
                      userInfo
                    );
                    return domainResult.domain || <span style={{ color: '#ef4444' }}>Not set</span>;
                  })()}
                </strong>
              </p>
              {selectedCreativeForDetail.dspCreativeId && (
                <p className="text-xs" style={{ marginTop: 4, color: '#64748b', fontFamily: 'monospace' }}>
                  DSP ID: {selectedCreativeForDetail.dspCreativeId}
                </p>
              )}
              {isCreativeSubmittedToPerion(selectedCreativeForDetail) && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary mt-2"
                  disabled={loading}
                  onClick={() => handleResubmitCreative(selectedCreativeForDetail)}
                >
                  <FaRedo /> Resubmit with new domain
                </button>
              )}
              
              <div className="mt-4">
                <h4>Campaign Date Overrides</h4>
                <div className="date-inputs">
                  <div>
                    <label>Start Date</label>
                    <input type="date" className="input" value={creativeDetailStartDate} onChange={e => setCreativeDetailStartDate(e.target.value)} />
                  </div>
                  <div>
                    <label>End Date</label>
                    <input type="date" className="input" value={creativeDetailEndDate} onChange={e => setCreativeDetailEndDate(e.target.value)} />
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <h4>Custom Schedule <button className="btn btn-xs btn-secondary" onClick={() => setCreativeScheduleExpanded(!creativeScheduleExpanded)}>
                  {creativeScheduleExpanded ? 'Hide' : 'Show'}
                </button></h4>
                {creativeScheduleExpanded && (
                  <div className="schedule-grid-compact">
                    {creativeTimeSegmentsByDay.map((daySegs, dayIndex) => (
                      <div key={dayIndex} className="schedule-day-compact">
                        <div className="schedule-day-header-compact">
                          <strong>{daysOfWeekShort[dayIndex]}</strong>
                          <button className="btn btn-xs" onClick={() => {
                            const newGrid = [...creativeTimeSegmentsByDay];
                            newGrid[dayIndex] = [...newGrid[dayIndex], { id: generateTimeSegmentId(), day: dayIndex, startTime: '09:00', endTime: '17:00' }];
                            setCreativeTimeSegmentsByDay(newGrid);
                          }}>
                            <FaPlus />
                          </button>
                        </div>
                        {daySegs.map(seg => (
                          <div key={seg.id} className="schedule-segment-compact">
                            <input type="time" className="input input-xs" value={seg.startTime} onChange={e => {
                              const newGrid = [...creativeTimeSegmentsByDay];
                              const segment = newGrid[dayIndex].find(s => s.id === seg.id);
                              if (segment) segment.startTime = normalizeTimeString(e.target.value);
                              setCreativeTimeSegmentsByDay(newGrid);
                            }} />
                            <span>—</span>
                            <input type="time" className="input input-xs" value={seg.endTime} onChange={e => {
                              const newGrid = [...creativeTimeSegmentsByDay];
                              const segment = newGrid[dayIndex].find(s => s.id === seg.id);
                              if (segment) segment.endTime = normalizeTimeString(e.target.value);
                              setCreativeTimeSegmentsByDay(newGrid);
                            }} />
                            <button className="btn btn-xs btn-danger" onClick={() => {
                              const newGrid = [...creativeTimeSegmentsByDay];
                              newGrid[dayIndex] = newGrid[dayIndex].filter(s => s.id !== seg.id);
                              setCreativeTimeSegmentsByDay(newGrid);
                            }}>
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={closeCreativeDetailModal}>Cancel</button>
          <button className="btn btn-primary" onClick={saveCreativeDetailSchedule} disabled={savingCreativeDetail}>
            {savingCreativeDetail ? <FaSpinner className="fa-spin" /> : 'Save'}
          </button>
        </div>
      </ReactModal>

      {showWarmupModal && (
        <div
          className="hivestack-approve-layer"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !warmupLoading) setShowWarmupModal(false);
          }}
        >
          <div
            className="hivestack-approve-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="warmup-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hivestack-approve-dialog-header">
              <h2 id="warmup-modal-title" className="hivestack-approve-dialog-title">Create warm-up campaign</h2>
              <button
                type="button"
                className="campaign-detail-close"
                disabled={warmupLoading}
                onClick={() => setShowWarmupModal(false)}
                aria-label="Close"
              >
                <FaClose />
              </button>
            </div>
            <div className="hivestack-approve-dialog-body">
              <p className="text-sm" style={{ marginBottom: 12, color: '#444' }}>
                Pick the sign this warm-up should bid on. Creatives, deals, advertiser domain, and bid params are copied from this campaign onto that sign only.
              </p>
              <label className="label-sm">Sign</label>
              <select
                className="input"
                style={{ width: '100%', marginTop: 4, marginBottom: 12 }}
                value={warmupSignKey}
                onChange={(e) => setWarmupSignKey(e.target.value)}
                disabled={warmupLoading}
              >
                <option value="" disabled>Select a sign</option>
                {warmupSignOptions.map((opt) => (
                  <option key={opt.key} value={opt.key}>{opt.label}</option>
                ))}
              </select>
              <p className="text-xs" style={{ color: '#64748b' }}>
                Budget is 2× cost-per-play for the selected sign. Pacing ASAP. Runs 3 days. Requires an advertiser domain.
              </p>
            </div>
            <div className="hivestack-approve-dialog-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowWarmupModal(false)} disabled={warmupLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleCreateWarmupCampaign} disabled={warmupLoading || !warmupSignKey}>
                {warmupLoading ? <FaSpinner className="fa-spin" /> : 'Create warm-up'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showIabPickerModal && (
        <div
          className="hivestack-approve-layer"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeIabPicker();
          }}
        >
          <div
            className="hivestack-approve-dialog iab-picker-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="iab-picker-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hivestack-approve-dialog-header">
              <h2 id="iab-picker-title" className="hivestack-approve-dialog-title">
                Select IAB Category
              </h2>
              <button type="button" className="campaign-detail-close" onClick={closeIabPicker} aria-label="Close">
                <FaClose />
              </button>
            </div>
            <div className="hivestack-approve-dialog-body">
              <p className="text-sm" style={{ marginBottom: 12, color: '#64748b' }}>
                Search by code or name. Type to narrow the list — all matches are shown while searching.
              </p>
              <div className="iab-picker-search-wrap">
                <FaSearch className="iab-picker-search-icon" />
                <input
                  ref={iabSearchInputRef}
                  type="text"
                  className="input"
                  style={{ width: '100%', paddingLeft: 36 }}
                  placeholder="Search IAB categories by code or name…"
                  value={iabSearchTerm}
                  onChange={(e) => setIabSearchTerm(e.target.value)}
                  disabled={iabCategoriesLoading}
                />
              </div>
              <div className="iab-picker-list">
                {iabCategoriesLoading ? (
                  <div className="iab-picker-loading">
                    <FaSpinner className="fa-spin" />
                    <span>Loading categories…</span>
                  </div>
                ) : filteredIabCategories.length === 0 ? (
                  <div className="iab-picker-empty">
                    {String(iabSearchTerm || '').trim()
                      ? 'No categories match your search.'
                      : 'No categories available.'}
                  </div>
                ) : (
                  filteredIabCategories.map((c) => {
                    const code = normalizeStoredIabCode(c?.code);
                    const name = String(c?.name || '').trim();
                    const selected = normalizeStoredIabCode(pendingIabCode) === code;
                    return (
                      <button
                        key={code || name}
                        type="button"
                        className={`iab-picker-row${selected ? ' iab-picker-row-selected' : ''}`}
                        onClick={() => setPendingIabCode(code)}
                      >
                        <span className="iab-picker-code">{code}</span>
                        <span className="iab-picker-name">{name}</span>
                      </button>
                    );
                  })
                )}
              </div>
              {!iabCategoriesLoading && !String(iabSearchTerm || '').trim() && (iabCategories?.length || 0) > 150 && (
                <p className="text-xs" style={{ marginTop: 8, color: '#64748b' }}>
                  Showing first 150 categories — type to search the full list.
                </p>
              )}
            </div>
            <div className="hivestack-approve-dialog-footer">
              <button type="button" className="btn btn-secondary" onClick={closeIabPicker} disabled={iabCategoriesLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={confirmIabPicker} disabled={iabCategoriesLoading}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {approveHivestackModal && (
        <div
          className="hivestack-approve-layer"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !loading) setApproveHivestackModal(null);
          }}
        >
          <div
            className="hivestack-approve-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hivestack-approve-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="hivestack-approve-dialog-header">
              <h2 id="hivestack-approve-title" className="hivestack-approve-dialog-title">
                {approveHivestackModal.isResubmit ? 'Resubmit with new domain' : 'Approve & submit to Hivestack'}
              </h2>
              <button
                type="button"
                className="campaign-detail-close"
                disabled={loading}
                onClick={() => setApproveHivestackModal(null)}
                aria-label="Close"
              >
                <FaClose />
              </button>
            </div>
            <div className="hivestack-approve-dialog-body">
              <p className="text-sm" style={{ marginBottom: 12, color: '#444' }}>
                {approveHivestackModal.isResubmit
                  ? 'Hivestack cannot change the advertiser domain on an existing creative ID. This will POST the same uploaded asset with the new domain and a slightly different DSP ID (suffix _R).'
                  : 'Confirm the advertiser domain sent to Hivestack. You can edit it below before submitting.'}
              </p>
              {approveHivestackModal.isResubmit && approveHivestackModal.creative?.dspCreativeId && (
                <p className="text-xs" style={{ marginBottom: 12, color: '#64748b', fontFamily: 'monospace' }}>
                  Current DSP ID: {approveHivestackModal.creative.dspCreativeId}
                </p>
              )}
              {approveHivestackModal.sourceLabel ? (
                <p className="text-sm" style={{ marginBottom: 12, color: '#64748b' }}>
                  Prefilled from: <strong>{approveHivestackModal.sourceLabel}</strong>
                </p>
              ) : (
                <p className="text-sm" style={{ marginBottom: 12, color: '#92400e' }}>
                  No domain found on this creative or campaign — enter the advertiser domain manually.
                </p>
              )}
              {(campaign.clientWebsite ||
                campaign.clientDomain ||
                campaign.client_domain ||
                campaign.advertiser_domain ||
                campaign.advertiserDomain) && (
                <p className="text-xs" style={{ marginBottom: 8, color: '#64748b' }}>
                  Campaign advertiser / client fields:{' '}
                  {[
                    campaign.clientWebsite,
                    campaign.clientDomain,
                    campaign.client_domain,
                    campaign.advertiser_domain,
                    campaign.advertiserDomain,
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </p>
              )}
              <label className="label-sm">Advertiser domain</label>
              <input
                type="text"
                className="input"
                style={{ width: '100%', marginTop: 4 }}
                placeholder="example.com"
                value={approveHivestackModal.domainDraft}
                onChange={(e) =>
                  setApproveHivestackModal((prev) =>
                    prev ? { ...prev, domainDraft: e.target.value } : prev
                  )
                }
                disabled={loading}
                autoComplete="off"
              />
            </div>
            <div className="hivestack-approve-dialog-footer">
              <button type="button" className="btn btn-secondary" disabled={loading} onClick={() => setApproveHivestackModal(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" disabled={loading} onClick={handleConfirmApproveHivestack}>
                {loading ? <FaSpinner className="fa-spin" /> : approveHivestackModal.isResubmit ? <><FaRedo /> Resubmit</> : <><FaCheck /> Approve &amp; submit</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </ReactModal>
  );
};

export default CampaignDetail;
